import { collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, Timestamp, QueryDocumentSnapshot, DocumentData, onSnapshot, serverTimestamp, addDoc, writeBatch, DocumentReference, Firestore, runTransaction, orderBy, QueryConstraint, arrayUnion, getCountFromServer, deleteField } from 'firebase/firestore';
import { IShipmentRepository } from '../../domain/repositories/IShipmentRepository';
import { Shipment } from '../../domain/entities/Shipment';
import { Package, IPackageModal } from '../../domain/entities/Package';
import { IContainer } from '../../domain/entities/Container';
import { FirebaseProvider } from '../providers/FirebaseProvider';
import { ShipmentChange } from '../../domain/entities/ShipmentChange';
import { shipmentEvents } from '@/config/analytics';
import { 
  getStorage, 
  ref as storageRef, 
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
  uploadBytesResumable
} from 'firebase/storage';
import type { ImportMapping } from '../../domain/types/ImportMapping';
import { INotification } from '../../domain/entities/Notification';
import type { ShipmentFile, CreateShipmentFileDTO } from '../../domain/entities/ShipmentFile';
import { simpleCache } from '../../utils/simpleCache';
import { resolveMimeType } from '../../utils/mimeType';
import { shipmentFileStoragePath, storagePathIsShared } from '../../utils/shipmentFileStorage';
import { isShipmentLocked, ShipmentLockedError, type ShipmentActor } from '../../utils/shipmentLock';
import { canCreateShipments, resolveCompanyPlan, shipmentsCreatedFromCounter, ShipmentLimitReachedError } from '../../utils/companyPlan';

interface UserDocument {
  role?: string;
  name?: string;
  lastName?: string;
  email?: string;  
}

export interface IShipmentRepository {
  findAll(filters?: Partial<Shipment>, companyId?: string): Promise<Shipment[]>;
  findById(id: string, companyId?: string): Promise<Shipment | null>;
  create(shipment: Omit<Shipment, 'id'>, companyId?: string, userId?: string): Promise<Shipment>;
  update(id: string, shipment: Partial<Shipment>, companyId?: string): Promise<Shipment>;
  updateShipment(
    shipmentId: string, 
    updates: Partial<Shipment>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void>;
  delete(id: string, companyId?: string, userId?: string, userName?: string, userLastName?: string): Promise<void>;
  count(filters?: Partial<Shipment>, companyId?: string): Promise<number>;
  getNextShipmentNumber(companyId?: string): Promise<string>;

  // Active Users methods
  registerActiveUser(
    shipmentId: string,
    userId: string,
    userName: string,
    companyId?: string,
    lastName?: string
  ): Promise<void>;
  removeActiveUser(shipmentId: string, userId: string, companyId?: string): Promise<void>;
  subscribeToActiveUsers(
    shipmentId: string,
    callback: (users: Array<{
      userId: string
      userName: string
      lastName?: string
      role?: string
      lastSeen: Date
      createdAt: Date
      isActive: boolean
    }>) => void,
    companyId?: string
  ): () => void;

  // Subscription methods
  subscribeToShipment(
    shipmentId: string,
    companyId: string | undefined,
    callback: (shipment: Shipment) => void
  ): () => void;

  // Packages methods
  getPackages(shipmentId: string, companyId?: string): Promise<Package[]>;
  addPackage(
    shipmentId: string, 
    packageData: Omit<IPackageModal, 'id'>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<string>;
  updatePackage(
    shipmentId: string, 
    packageId: string, 
    packageData: Partial<Package>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void>;
  deletePackage(
    shipmentId: string, 
    packageId: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void>;
  listenToPackages(
    shipmentId: string, 
    callback: (packages: Package[]) => void,
    companyId?: string
  ): Promise<() => void>;

  // Container methods
  getContainers(shipmentId: string, companyId?: string): Promise<IContainer[]>;
  addContainer(
    shipmentId: string, 
    containerData: Omit<IContainer, 'id'>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    isImported?: boolean
  ): Promise<string>;
  updateContainer(
    shipmentId: string, 
    containerId: string, 
    containerData: Partial<IContainer>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void>;
  deleteContainer(
    shipmentId: string, 
    containerId: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void>;

  // Comments methods
  addComment(
    shipmentId: string,
    commentData: {
      comment: string;
      fullName: string;
      createdAt: Date;
      userId: string;
    },
    companyId?: string
  ): Promise<string>;
  
  updateComment(
    shipmentId: string,
    commentId: string,
    commentData: {
      comment: string;
    },
    companyId?: string
  ): Promise<void>;
  
  deleteComment(
    shipmentId: string,
    commentId: string,
    companyId?: string
  ): Promise<void>;
  
  listenToShipmentComments(
    shipmentId: string,
    callback: (comments: Array<{
      id: string;
      comment: string;
      fullName: string;
      createdAt: Date;
      userId: string;
    }>) => void,
    companyId?: string
  ): Promise<() => void>;

  importShipments(
    shipments: Partial<Shipment>[], 
    file: File,
    companyId?: string, 
    userId?: string
  ): Promise<Shipment[]>;  
  saveImportMapping(mapping: Omit<ImportMapping, 'id'>): Promise<string>;
  getImportMappingByHash(userId: string, fileHash: string): Promise<ImportMapping | null>;
  updateImportMapping(id: string, mapping: Partial<ImportMapping>): Promise<void>;
  listImportMappings(userId: string): Promise<ImportMapping[]>;  
  checkFileExists(userId: string, fileHash: string): Promise<boolean>;
  getFileUrl(userId: string, fileHash: string): Promise<string>;
  storeFile(userId: string, file: File, fileHash: string): Promise<string>;

  importShipmentsBatch(
    shipments: Partial<Shipment>[],
    companyId: string
  ): Promise<Shipment[]>;

  getNotifications(shipmentId: string, companyId: string, userId: string): Promise<INotification[]>;

  // Change History methods
  addShipmentChangeRecord(companyId: string, shipmentId: string, changeRecord: ShipmentChange): Promise<void>;

  // File review methods
  markShipmentFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string,
    reviewer: { uid: string; name: string; lastName: string }
  ): Promise<void>;
  unmarkShipmentFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<void>;
}

export class FirestoreShipmentRepository implements IShipmentRepository {
  private db: Firestore;
  private shipmentsCollection = 'shipments';

  constructor(db?: Firestore) {
    this.db = db || FirebaseProvider.getInstance().getFirestore();
  }

  private getCollectionRef(companyId?: string) {
    if (!companyId) {
      throw new Error('Company ID is required')
    }    
    return collection(this.db, 'Companies', companyId, this.shipmentsCollection)
  }

  private toFirestore(shipment: Partial<Shipment>) {
    const updates: any = { ...shipment }
    
    // Función auxiliar para validar y convertir fechas
    const safeDateConversion = (date: any): Date | null => {
      if (!date) return null;
      try {
        // Si ya es un Date, retornarlo
        if (date instanceof Date) return date;
        
        // Si es un Timestamp, convertirlo a Date
        if (date?.toDate) return date.toDate();
        
        // Si es un string, intentar parsearlo
        if (typeof date === 'string') {
          const parsedDate = new Date(date);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        }
        
        return null;
      } catch (error) {
        console.error('Error converting date:', error);
        return null;
      }
    };

    // Función para formatear fechas a string ISO
    const formatDateToISOString = (date: any): string | null => {
      if (!date) return null;
      try {
        // Si ya es un string con formato válido, devolverlo tal cual
        if (typeof date === 'string') {
          const parsedDate = new Date(date);
          if (!isNaN(parsedDate.getTime())) {
            // Verificar si ya tiene el formato correcto YYYY-MM-DDTHH:mm:ss
            const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
            if (isoRegex.test(date)) {
              return date;
            }
            // Si no tiene el formato correcto, convertirlo
            return parsedDate.toISOString().slice(0, 19);
          }
        }
        
        // Si es un Date, convertirlo a string ISO
        if (date instanceof Date) {
          return date.toISOString().slice(0, 19);
        }
        
        // Si es un Timestamp, convertirlo a Date y luego a string ISO
        if (date?.toDate) {
          return date.toDate().toISOString().slice(0, 19);
        }
        
        return null;
      } catch (error) {
        console.error('Error formatting date to ISO string:', error);
        return null;
      }
    };
    
    // Convertir fechas principales del shipment (mantener como Timestamp para compatibilidad)
    if (updates.shipmentDate) {
      const date = safeDateConversion(updates.shipmentDate);
      if (date) updates.shipmentDate = formatDateToISOString(date);
    }
    if (updates.requestedPickupDate) {
      const date = safeDateConversion(updates.requestedPickupDate);
      if (date) updates.requestedPickupDate = Timestamp.fromDate(date);
    }
    if (updates.requestedDeliveryDate) {
      const date = safeDateConversion(updates.requestedDeliveryDate);
      if (date) updates.requestedDeliveryDate = Timestamp.fromDate(date);
    }
    if (updates.eta) {
      const date = safeDateConversion(updates.eta);
      if (date) updates.eta = Timestamp.fromDate(date);
    }
    
    // Inicializar milestones si no existe
    if (!updates.milestones && shipment.milestones) {
      updates.milestones = {};
    }
    
    // Convertir fechas de milestones a formato string ISO
    if (updates.milestones) {
      const milestoneDateFields = [
        'requestedPickupDate', 
        'requestedDeliveryDate', 
        'pickupDate', 
        'etd', 
        'atd', 
        'eta', 
        'ata', 
        'deliveryDate',
        'estimatedPickupDate',
        'actualPickupDate',
        'estimatedDeliveryDate',
        'actualDeliveryDate',
        'freeStorageDate',
        'freeRetentionDate',
        'loadingDate',
        'portReturnDate',
        'portPickupDate'
      ];
      
      milestoneDateFields.forEach(field => {
        if (updates.milestones[field] !== undefined) {
          const dateString = formatDateToISOString(updates.milestones[field]);
          if (dateString) {
            updates.milestones[field] = dateString;
          } else if (updates.milestones[field] === null) {
            updates.milestones[field] = null;
          }
        }
      });
    }
    
    // Manejar actualizaciones de milestones individuales
    Object.keys(updates).forEach(key => {
      if (key.startsWith('milestones.')) {
        const milestoneField = key.split('.')[1];
        const value = updates[key];
        
        // Convertir fechas de milestones a formato string ISO
        if (value !== null && [
          'requestedPickupDate', 
          'requestedDeliveryDate', 
          'pickupDate', 
          'etd', 
          'atd', 
          'eta', 
          'ata', 
          'deliveryDate',
          'estimatedPickupDate',
          'actualPickupDate',
          'estimatedDeliveryDate',
          'actualDeliveryDate',
          'freeStorageDate',
          'freeRetentionDate',
          'loadingDate',
          'portReturnDate',
          'portPickupDate'
        ].includes(milestoneField)) {
          const dateString = formatDateToISOString(value);
          if (dateString) {
            updates[key] = dateString;
          }
        }
      }
    });

    // Siempre incluir lastUpdate
    updates.lastUpdate = Timestamp.now();
    
    return updates;
  }

  private fromFirestore(data: any): Shipment {
    // ✅ Extraer campos del sistema conocidos para no sobrescribirlos con el spread final
    const systemFields = {
      milestones: {
        ...data.milestones,
        requestedPickupDate: data.milestones?.requestedPickupDate ? data.milestones?.requestedPickupDate : null,
        requestedDeliveryDate: data.milestones?.requestedDeliveryDate ? data.milestones?.requestedDeliveryDate : null,
        pickupDate: data.milestones?.pickupDate ? data.milestones?.pickupDate : null,
        etd: data.milestones?.etd ? data.milestones?.etd : null,
        atd: data.milestones?.atd ? data.milestones?.atd : null,
        eta: data.milestones?.eta ? data.milestones?.eta : null,
        ata: data.milestones?.ata ?? null,
        deliveryDate: data.milestones?.deliveryDate ? data.milestones?.deliveryDate : null,
        estimatedPickupDate: data.milestones?.estimatedPickupDate ? data.milestones?.estimatedPickupDate : null,
        estimatedDeliveryDate: data.milestones?.estimatedDeliveryDate ? data.milestones?.estimatedDeliveryDate : null,
        
        freeStorageDate: data.milestones?.freeStorageDate ? data.milestones?.freeStorageDate : null,
        freeRetentionDate: data.milestones?.freeRetentionDate ? data.milestones?.freeRetentionDate : null,
        loadingDate: data.milestones?.loadingDate ? data.milestones?.loadingDate : null,
        portReturnDate: data.milestones?.portReturnDate ? data.milestones?.portReturnDate : null,
        portPickupDate: data.milestones?.portPickupDate ? data.milestones?.portPickupDate : null
      },
      shipmentDate: data.shipmentDate ? data.shipmentDate : null,
      requestedPickupDate: data.requestedPickupDate ? data.requestedPickupDate : null,
      requestedDeliveryDate: data.requestedDeliveryDate ? data.requestedDeliveryDate : null,
      lastUpdate: data.lastUpdate ? data.lastUpdate : null,
      eta: data.eta ?? null,
    };
    
    // ✅ Retornar todos los campos de data (incluidos los dinámicos) + sobrescribir los del sistema
    return {
      ...data,
      ...systemFields,
      consolidationType: data.consolidationType || 'standalone'
    } as Shipment;
  }

  private async getAndIncrementCounterBatch(companyId: string, totalCount: number): Promise<number[]> {
    const configRef = doc(this.db, `Companies/${companyId}/config/GLOBAL_CONFIG`);
    
    // Usar transacción para garantizar atomicidad y consistencia
    return await runTransaction(this.db, async (transaction) => {
      const configSnap = await transaction.get(configRef);
      const currentCounter = configSnap.exists() ? (configSnap.data()?.shipmentCounter || 1) : 1;

      // Free Premium: el contador es el cupo histórico; si no alcanza, no se crea nada
      const companySnap = await transaction.get(doc(this.db, 'Companies', companyId));
      const plan = resolveCompanyPlan(companySnap.data());
      if (!canCreateShipments(plan, shipmentsCreatedFromCounter(currentCounter), totalCount)) {
        throw new ShipmentLimitReachedError(companyId);
      }

      // set + merge en vez de update: si GLOBAL_CONFIG aún no existe (compañía
      // nueva), update lanza not-found y el primer embarque no se puede crear
      transaction.set(configRef, {
        shipmentCounter: currentCounter + totalCount
      }, { merge: true });
      
      // Generar y retornar el rango de contadores
      return Array.from({ length: totalCount }, (_, i) => currentCounter + i);
    });
  }

  async create(shipment: Omit<Shipment, 'id'>, companyId?: string, _userId?: string): Promise<Shipment> {
    
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    // Obtener y actualizar el contador de la compañía
    const counter = await this.getAndIncrementCounterBatch(companyId, 1);
    
    // Generar el liberationNumber con formato de 8 dígitos
    const liberationNumber = counter[0].toString().padStart(8, '0')
    
    // Generar el idNumber usando el mismo contador
    const idNumber = `SHIP-${counter[0].toString().padStart(7, '0')}`
    
    const docRef = doc(this.getCollectionRef(companyId))
    const newShipment = { 
      ...shipment, 
      id: docRef.id,
      liberationNumber,
      idNumber,
      createdAt: new Date()
    }

    await setDoc(docRef, this.toFirestore(newShipment))
    shipmentEvents.shipmentCreated(newShipment.id)
    return newShipment
  }

  /** Query de la lista: mismo orden y filtros para findAll y count. */
  private buildListQuery(filters?: Partial<Shipment>, companyId?: string) {
    let queryConstraints: QueryConstraint[] = [

        orderBy('createdAt', 'desc'), // Ordenar por fecha de creación descendente
    ];
    
    if (filters) {
        // Convert filters to plain object and remove empty values
        const plainFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, any>);
        
        // Apply non-empty filters
        Object.entries(plainFilters).forEach(([key, value]) => {
            queryConstraints.push(where(key, '==', value));
        });
    }

    return query(this.getCollectionRef(companyId), ...queryConstraints);
  }

  async findAll(filters?: Partial<Shipment>, companyId?: string): Promise<Shipment[]> {    
    const querySnapshot = await getDocs(this.buildListQuery(filters, companyId));    
    
    const shipments = querySnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => 

        this.fromFirestore({ id: doc.id, ...doc.data() })
    );
    
    return shipments;
  }

  async findById(id: string, companyId?: string): Promise<Shipment | null> {
    const docRef = doc(this.getCollectionRef(companyId), id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.fromFirestore({ id: docSnap.id, ...docSnap.data() });
  }

  async update(id: string, shipment: Partial<Shipment>, companyId?: string): Promise<Shipment> {
    const docRef = doc(this.getCollectionRef(companyId), id);
    await updateDoc(docRef, this.toFirestore(shipment));
    const updated = await this.findById(id, companyId);
    if (!updated) throw new Error('Shipment not found after update');
    return updated;
  }

  async updateShipment(shipmentId: string, updates: Partial<Shipment>, companyId?: string, userId?: string, name?: string, lastName?: string): Promise<void> {
    try {
      const docRef = doc(this.getCollectionRef(companyId), shipmentId);
      const currentDoc = await getDoc(docRef);
      if (!currentDoc.exists()) {
        throw new Error('Shipment not found');
      }
      if (isShipmentLocked(currentDoc.data())) {
        throw new ShipmentLockedError(shipmentId);
      }

      // Mantener el updatedBy existente si no se proporcionan los datos del usuario
      const firestoreData = this.toFirestore({
        ...updates,
        updatedBy: updates.updatedBy || (userId && name && lastName ? {
          id: userId,
          name: name,
          lastName: lastName
        } : null)
      });

      await updateDoc(docRef, firestoreData);

      // 3. Registrar los cambios en la subcolección changesHistory
      if (userId && name && lastName) {
        
        // Primero, definimos los campos especiales que queremos tratar de manera diferente
        const arrayFields = ['customerReferenceNumber', 'relatedReferenceNumber', 'referenceThreeValue', 'referenceFourValue'];

        // En la función flattenObject, agregamos una condición especial para estos campos
        const flattenObject = (obj: any, prefix = '') => {
          return Object.keys(obj).reduce((acc: any, key: string) => {
            const newKey = prefix ? `${prefix}.${key}` : key;
            const value = obj[key];
            
            // Tratamiento especial para los campos de tipo array que nos interesan
            if (arrayFields.includes(key) && Array.isArray(value)) {
              // Para estos campos, guardamos el array completo sin aplanar
              acc[newKey] = value;
              return acc;
            }

            // El resto del código existente para el flatten...
            // if (value?.toDate) {
            //   acc[newKey] = value.toDate();
            //   return acc;
            // }

            // if (value instanceof Date) {
            //   acc[newKey] = value;
            //   return acc;
            // }

            if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
              Object.assign(acc, flattenObject(value, newKey));
            } else {
              acc[newKey] = value;
            }
            return acc;
          }, {});
        };

        // Aplanar los objetos actuales y actualizados
        const flatCurrentData = flattenObject(currentDoc.data());
        const flatUpdates = flattenObject(updates);

        // Modificamos la parte donde procesamos los cambios
        let changes = Object.keys(flatUpdates)
          .filter(field => field !== 'lastUpdate')
          .flatMap(field => {
            const oldValue = flatCurrentData[field];
            const newValue = flatUpdates[field];

            // Si es uno de nuestros campos especiales de array
            if (arrayFields.includes(field)) {
              if (!Array.isArray(oldValue) && !Array.isArray(newValue)) {
                return [];
              }

              const oldSet = new Set(oldValue || []);
              const newSet = new Set(newValue || []);
              const changes: any[] = [];

              // Valores que fueron eliminados
              for (const value of oldSet) {
                if (!newSet.has(value)) {
                  changes.push({
                    field,
                    oldValue: value,
                    newValue: null
                  });
                }
              }

              // Valores que fueron agregados o modificados
              for (const value of newSet) {
                if (!oldSet.has(value)) {
                  changes.push({
                    field,
                    oldValue: null,
                    newValue: value
                  });
                }
              }

              return changes;
            }

            // El resto del código existente para otros campos...
            // Asegurarse de que las fechas estén en formato Date
            let processedOldValue = oldValue;
            let processedNewValue = newValue;
            

            // Si algún valor es undefined, usar null en su lugar
            processedOldValue = processedOldValue === undefined ? null : processedOldValue;
            processedNewValue = processedNewValue === undefined ? null : processedNewValue;

            // Para campos que sabemos son fechas, asegurarnos de que se manejen como Date
            // const dateFields = ['shipmentDate', 'requestedPickupDate', 'requestedDeliveryDate', 'eta', 'createdAt', 'updatedAt'];
            // const milestoneDateFields = ['pickupDate', 'etd', 'atd', 'eta', 'ata', 'deliveryDate', 'estimatedPickupDate', 'actualPickupDate', 'estimatedDeliveryDate', 'actualDeliveryDate', 'freeStorageDate', 'freeRetentionDate', 'loadingDate', 'portReturnDate', 'portPickupDate'];
            
            // // Mejorar la detección de campos de fecha
            // const isDateField = (field: string) => {
            //   // Extraer el nombre del campo sin el prefijo de milestones
            //   const fieldName = field.includes('.') ? field.split('.').pop()! : field;
              
            //   // Verificar si el campo está en alguna de las listas de campos de fecha
            //   return dateFields.includes(fieldName) || milestoneDateFields.includes(fieldName);
            // };
          
            // if (isDateField(field)) {              
            //   if (processedOldValue) processedOldValue = (processedOldValue instanceof Date ? processedOldValue : new Date(processedOldValue)).toISOString().split('T')[0];
            //   if (processedNewValue) processedNewValue = (processedNewValue instanceof Date ? processedNewValue : new Date(processedNewValue)).toISOString().split('T')[0];
            // }

            return [{
              field,
              oldValue: processedOldValue,
              newValue: processedNewValue
            }];
          })
          .filter(change => {
            // Solo registrar cambios reales y asegurarse que no hay undefined
            const hasValidValues = change.oldValue !== undefined && change.newValue !== undefined;
            const hasChanged = JSON.stringify(change.oldValue) !== JSON.stringify(change.newValue);
            return hasValidValues && hasChanged;
          });

        // Agrupar cambios que ocurren simultáneamente en el mismo valor
        const groupedChanges = changes.reduce((acc: any[], change) => {
          // Buscar si existe un cambio del mismo campo con los mismos valores
          const existingChange = acc.find(c =>
            c.field === change.field &&
            JSON.stringify(c.newValue) === JSON.stringify(change.newValue) &&
            JSON.stringify(c.oldValue) === JSON.stringify(change.oldValue)
          );

          if (existingChange) {
            // Si existe un cambio con el mismo valor, no lo agregamos
            return acc;
          }

          // Si no existe, agregamos el cambio
          return [...acc, change];
        }, []);

        if (groupedChanges.length > 0) {
          const changeRecord: ShipmentChange = {
            timestamp: new Date(),
            userId,
            name,
            lastName,
            changes: groupedChanges,
            action: 'UPDATE',
            description: this.generateChangeDescription(groupedChanges, currentDoc.data())
          };

          const changesCollectionRef = collection(docRef, 'changesHistory');
          await addDoc(changesCollectionRef, {
            ...changeRecord,
            timestamp: Timestamp.fromDate(changeRecord.timestamp)
          });
        }
      }
    } catch (error) {
      throw error;
    }
  }

  private generateChangeDescription(changes: any[], shipmentData: any): string {
    const firstChange = changes[0];
    if (!firstChange) return 'Se actualizó el embarque';

    if (firstChange.field.startsWith('container.')) {
      return `Actualizó un contenedor`;
    } else if (firstChange.field.startsWith('package.')) {
      return `Actualizó un paquete`;
    } 

    return 'Realizó un cambio en el embarque';
  }

  async delete(id: string, companyId?: string, userId?: string, userName?: string, userLastName?: string): Promise<void> {
    if (!companyId) {
      throw new Error('Company ID is required')
    }

    const docRef = doc(this.getCollectionRef(companyId), id)
    const shipmentDoc = await getDoc(docRef)

    if (!shipmentDoc.exists()) {
      throw new Error('Shipment not found')
    }
    if (isShipmentLocked(shipmentDoc.data())) {
      throw new ShipmentLockedError(id)
    }

    // Importante: agregar el ID al objeto shipment ya que Firestore no lo incluye automáticamente
    const shipmentData = {
      ...shipmentDoc.data(),
      id: shipmentDoc.id
    } as Shipment

    // ⭐ DESVINCULAR ÓRDENES DE COMPRA ASOCIADAS
    // Buscar todas las OCs que tengan este shipmentId y limpiar su vinculación
    await this.unlinkPurchaseOrdersFromShipment(id, companyId)

    // ⭐ DESVINCULAR CORREOS ASOCIADOS
    // Buscar todos los correos que tengan este shipmentId y limpiar su vinculación
    await this.unlinkEmailsFromShipment(id, shipmentData.idNumber || id, companyId)

    // Guardar log en deletedShipments antes de eliminar
    await this.saveDeletedShipmentLog(shipmentData, companyId, userId, userName, userLastName)

    const batch = writeBatch(this.db)

    // Eliminar el shipment y sus colecciones relacionadas
    batch.delete(docRef)
    await this.deleteCollectionRecursively(docRef)
    
    // Commit de la transacción
    await batch.commit()
  }

  /**
   * Desvincula todas las órdenes de compra asociadas a un embarque
   * Se ejecuta antes de eliminar el embarque
   */
  private async unlinkPurchaseOrdersFromShipment(
    shipmentId: string,
    companyId: string
  ): Promise<void> {
    try {
      // Buscar todas las OCs asociadas a este embarque
      const posQuery = query(
        collection(this.db, `Companies/${companyId}/purchaseOrders`),
        where('shipmentId', '==', shipmentId)
      )
      
      const posSnapshot = await getDocs(posQuery)
      
      if (posSnapshot.empty) {
        return
      }


      // Desvincular cada OC en paralelo
      const unlinkPromises = posSnapshot.docs.map(async (poDoc) => {
        const poRef = doc(this.db, `Companies/${companyId}/purchaseOrders`, poDoc.id)
        
        await updateDoc(poRef, {
          shipmentId: null,
          shipmentNumber: null,
          updatedAt: new Date()
        })

      })

      await Promise.all(unlinkPromises)
      

      // ⭐ INVALIDAR CACHÉ DE PURCHASE ORDERS
      // Esto forzará a recargar los datos actualizados en la tabla de OCs
      simpleCache.clear(`purchase_orders_${companyId}`)
      simpleCache.clear(`po_items_${companyId}`)
    } catch (error) {
      console.error('❌ Error desvinculando órdenes de compra:', error)
      // No lanzamos el error para no interrumpir la eliminación del embarque
    }
  }

  /**
   * Desvincula todos los correos asociados a un embarque
   * Se ejecuta antes de eliminar el embarque
   */
  private async unlinkEmailsFromShipment(
    shipmentId: string,
    shipmentIdNumber: string,
    companyId: string
  ): Promise<void> {
    try {
      // Buscar todos los correos asociados a este embarque
      const emailsQuery = query(
        collection(this.db, `Companies/${companyId}/emails`),
        where('matchedShipmentId', '==', shipmentId)
      )
      
      const emailsSnapshot = await getDocs(emailsQuery)
      
      if (emailsSnapshot.empty) {
        return
      }


      // Desvincular cada correo en paralelo
      const unlinkPromises = emailsSnapshot.docs.map(async (emailDoc) => {
        const emailRef = doc(this.db, `Companies/${companyId}/emails`, emailDoc.id)
        
        await updateDoc(emailRef, {
          matchedShipmentId: null,
          matchedShipmentIdNumber: null,
          updatedAt: Timestamp.now()
        })

        const emailData = emailDoc.data()
      })

      await Promise.all(unlinkPromises)
      

      // ⭐ INVALIDAR CACHÉ DE EMAILS
      // Esto forzará a recargar los datos actualizados en el módulo de correos
      simpleCache.clear(`emails_${companyId}`)
    } catch (error) {
      console.error('❌ Error desvinculando correos:', error)
      // No lanzamos el error para no interrumpir la eliminación del embarque
    }
  }

  private async saveDeletedShipmentLog(
    shipment: Shipment, 
    companyId: string, 
    userId?: string, 
    userName?: string, 
    userLastName?: string
  ): Promise<void> {
    try {
      const deletedShipmentsRef = collection(this.db, 'Companies', companyId, 'deletedShipments')
      
      // Usar any para acceder a propiedades dinámicas que pueden no estar en la interfaz
      const shipmentData = shipment as any
      
      const deletedShipmentLog = {
        // Datos originales del embarque
        originalShipmentId: shipment.id,
        idNumber: shipment.idNumber,
        
        // Información de creación
        createdBy: shipment.createdBy || null,
        createdAt: shipment.createdAt || null,
        
        // Referencias principales
        mbl: shipment.mbl || null,
        booking: shipment.booking || null,
        hbl: shipment.hbl || null,
        
        // Fechas importantes
        eta: shipment.eta || null,
        etd: shipmentData.etd || null,
        ata: shipmentData.ata || null,
        atd: shipmentData.atd || null,
        
        // Ubicaciones
        origin: shipment.origin || null,
        destination: shipment.destination || null,
        pol: shipmentData.pol || null,
        pod: shipmentData.pod || null,
        
        // Información de transporte
        carrier: shipmentData.carrier || null,
        vessel: shipment.vessel || null,
        voyage: shipmentData.voyage || null,
        
        // Estado y tipo
        status: shipment.status || null,
        transportType: shipmentData.transportType || null,
        incoterm: shipment.incoterm || null,
        
        // Información del cliente
        shipper: shipmentData.shipper || null,
        consignee: shipmentData.consignee || null,
        notifyParty: shipmentData.notifyParty || null,
        
        // Información de eliminación
        deletedBy: {
          id: userId || 'UNKNOWN',
          name: userName || 'Unknown',
          lastName: userLastName || ''
        },
        deletedAt: serverTimestamp(),
        
        // Tracking y referencias adicionales
        isTracking: shipment.isTracking || false,
        assignedTo: shipment.assignedTo || null,
        
        // Guardar el objeto completo también para referencias futuras
        fullShipmentData: shipmentData
      }
      
      await addDoc(deletedShipmentsRef, deletedShipmentLog)
    } catch (error) {
      console.error('Error al guardar log de embarque eliminado:', error)
      // No lanzamos el error para no interrumpir la eliminación
    }
  }

  private async deleteCollectionRecursively(docRef: DocumentReference): Promise<void> {
    // Delete subcollections we know exist
    const knownCollections = ['packages', 'containers', 'activeUsers', 'changesHistory', 'containerTracking', 'tracking'];
    const batch = writeBatch(this.db);

    for (const collectionName of knownCollections) {
      const collectionRef = collection(docRef, collectionName);
      const documents = await getDocs(collectionRef);
      
      documents.forEach((doc) => {
        batch.delete(doc.ref);
      });
    }

    // Delete the main document
    batch.delete(docRef);

    // Commit the batch
    await batch.commit();
  }

  // Agregación del servidor: antes bajaba la colección completa para medir el largo.
  async count(filters?: Partial<Shipment>, companyId?: string): Promise<number> {
    const snapshot = await getCountFromServer(this.buildListQuery(filters, companyId));
    return snapshot.data().count;
  }


  async getNextShipmentNumber(companyId?: string): Promise<string> {
    const collectionRef = this.getCollectionRef(companyId);
    const querySnapshot = await getDocs(collectionRef);
    let maxNumber = 0;

    querySnapshot.forEach((doc) => {
      const idNumber = doc.data().idNumber;
      if (idNumber && idNumber.startsWith('SHIP-')) {
        const currentNumber = parseInt(idNumber.replace('SHIP-', ''));
        if (!isNaN(currentNumber) && currentNumber > maxNumber) {
          maxNumber = currentNumber;
        }
      }
    });

    const nextNumber = maxNumber + 1;
    return `SHIP-${nextNumber.toString().padStart(7, '0')}`;
  }

  subscribeToShipment(shipmentId: string, companyId: string | undefined, callback: (shipment: Shipment) => void): () => void {
    const docRef = doc(this.getCollectionRef(companyId), shipmentId);

    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const shipmentData = snapshot.data();
        callback(this.fromFirestore(shipmentData));
      }
    });
  }

  // Packages methods
  async getPackages(shipmentId: string, companyId?: string): Promise<Package[]> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const packagesCollectionRef = collection(shipmentRef, 'packages')
    const packagesSnapshot = await getDocs(packagesCollectionRef)
    
    return packagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Package))
  }

  async addPackage(
    shipmentId: string, 
    packageData: Omit<IPackageModal, 'id'>, 
    companyId?: string, 
    userId?: string, 
    name?: string, 
    lastName?: string
  ): Promise<string> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const packagesCollectionRef = collection(shipmentRef, 'packages')
    const newPackageRef = doc(packagesCollectionRef)
    
    const batch = writeBatch(this.db);

    // Preparar los datos del paquete con timestamps
    const now = Timestamp.now();
    const packageToSave = {
      ...packageData,
      id: newPackageRef.id,
      createdAt: now,
      updatedAt: now
    };

    // Agregar el paquete
    batch.set(newPackageRef, packageToSave);

    // Si hay información del usuario, registrar en el historial de cambios
    if (userId && name && lastName) {
      // Actualizar el documento principal del shipment
      batch.update(shipmentRef, {
        updatedAt: now,
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });

      // Registrar en el historial de cambios
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');

      // Función auxiliar para aplanar objetos anidados
      const flattenObject = (obj: any, prefix = '') => {
        return Object.keys(obj).reduce((acc: any, key: string) => {
          const newKey = prefix ? `${prefix}.${key}` : key;
          if (typeof obj[key] === 'object' && obj[key] !== null && !(obj[key] instanceof Date)) {
            Object.assign(acc, flattenObject(obj[key], newKey));
          } else {
            acc[newKey] = obj[key];
          }
          return acc;
        }, {});
      };

      // Aplanar el objeto del paquete
      const flatPackage = flattenObject(packageData);

      // Crear los registros de cambio
      const changes = Object.entries(flatPackage)
        .filter(([field, value]) => {
          // Excluir valores nulos, undefined o vacíos
          if (value === null || value === undefined || value === '') {
            return false;
          }

          // Excluir booleanos false (valores por defecto)
          if (value === false) {
            return false;
          }

          // Excluir campos de unidades de medida con valores por defecto
          if (field.toLowerCase().includes('uom') && ['°C', 'CM', 'KGM'].includes(value as string)) {
            return false;
          }

          // Excluir números que son 0 o negativos
          if (typeof value === 'number' && value <= 0) {
            return false;
          }

          return true;
        })
        .map(([field, value]) => ({
          field: `package.${newPackageRef.id}.${field}`,
          oldValue: null,
          newValue: value
        }));

        const changeData = {
          timestamp: now,
          type: 'PACKAGE_ADDED',
          userId,
          name,
          lastName,
          changes,
          source: 'USER',
          action: 'CREATE',
          description: packageData.reference 
            ? `Creó un paquete con número de referencia ${packageData.reference}`
            : 'Creó un paquete sin número de referencia'
        };

        batch.set(doc(changesCollectionRef), changeData);
    }

    await batch.commit();
    return newPackageRef.id;
  }

  async updatePackage(
    shipmentId: string, 
    packageId: string, 
    packageData: Partial<Package>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !packageId || !companyId) {
      throw new Error('Shipment ID, Package ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const packageRef = doc(collection(shipmentRef, 'packages'), packageId)
    
    try {
      // 1. Obtener el package actual antes de la actualización
      const currentDoc = await getDoc(packageRef)
      if (!currentDoc.exists()) {
        throw new Error('Package not found')
      }
      const currentData = currentDoc.data()

      // 2. Realizar la actualización del package
      const cleanedData = this.cleanObject(packageData)
      const updateData = {
        ...cleanedData,
        updatedAt: serverTimestamp()
      }
      await updateDoc(packageRef, updateData)

      // 3. Actualizar el documento principal del shipment para disparar las notificaciones
      if (userId && name && lastName) {
        await updateDoc(shipmentRef, {
          updatedAt: serverTimestamp(),
          updatedBy: {
            id: userId,
            name,
            lastName
          }
        })

        // 4. Registrar los cambios en la subcolección changesHistory del shipment
        // Filtrar solo los campos que realmente cambiaron
        let changes = Object.keys(packageData)
          .map(field => {
            const oldValue = currentData[field]
            const newValue = packageData[field]

            // Si los valores son iguales después de limpiarlos, retornar null
            if (this.areValuesEqual(oldValue, newValue)) {
              return null
            }

            // Limpiar los valores antes de guardarlos en el historial
            return {
              field: `package.${packageId}.${field}`,
              oldValue: this.cleanObject(oldValue),
              newValue: this.cleanObject(newValue)
            }
          })
          .filter((change): change is NonNullable<typeof change> => 
            change !== null && 
            !this.areValuesEqual(change.oldValue, change.newValue)
          )

        if (changes.length > 0) {
          const changeRecord: ShipmentChange = {
            timestamp: new Date(),
            userId,
            name,
            lastName,
            changes,
            action: 'UPDATE',
            description: currentData.reference 
              ? `Actualizó el paquete con número de referencia ${currentData.reference}`
              : 'Actualizó un paquete sin número de referencia'
          }

          const changesCollectionRef = collection(shipmentRef, 'changesHistory')
          await addDoc(changesCollectionRef, {
            ...changeRecord,
            timestamp: Timestamp.fromDate(changeRecord.timestamp)
          })
        }
      }
    } catch (error) {
      throw error
    }
  }

  async deletePackage(
    shipmentId: string, 
    packageId: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void> {
    if (!shipmentId || !packageId || !companyId) {
      throw new Error('Shipment ID, Package ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const packageRef = doc(collection(shipmentRef, 'packages'), packageId)
    
    // Get package data before deletion
    const packageSnap = await getDoc(packageRef);
    if (!packageSnap.exists()) {
      throw new Error('Package not found');
    }
    const packageData = packageSnap.data();

    // Delete the package
    await deleteDoc(packageRef);

    // Record the change in history if user info is provided
    if (userId && name && lastName) {
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      const changeData: ShipmentChange = {
        timestamp: new Date(),
        userId,
        name,
        lastName,
        action: 'DELETE',
        description: description || (packageData.reference 
          ? `Eliminó el paquete con número de referencia ${packageData.reference}`
          : 'Eliminó un paquete sin número de referencia'),
        changes: [{
          field: `package.${packageId}`,
          oldValue: this.cleanObject(packageData),
          newValue: null
        }]
      };

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      });

      // Update main shipment document to trigger notifications
      await updateDoc(shipmentRef, {
        lastUpdate: serverTimestamp(),
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });
    }
  }

  async listenToPackages(
    shipmentId: string, 
    callback: (packages: Package[]) => void,
    companyId?: string
  ): Promise<() => void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const packagesCollectionRef = collection(shipmentRef, 'packages')
    
    return onSnapshot(packagesCollectionRef, (snapshot) => {
      const packages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Package;
      });
      callback(packages);
    });
  }

  // Container methods
  async getContainers(shipmentId: string, companyId?: string): Promise<IContainer[]> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const containersCollectionRef = collection(shipmentRef, 'containers')
    const containersSnapshot = await getDocs(containersCollectionRef)
    
    return containersSnapshot.docs.map(doc => {
      const data = doc.data();
      const container: IContainer = {
        id: doc.id,
        number: data.number ?? '',
        loadingDate: data.loadingDate ?? '',
        events: data.events ?? [],
        type: data.type ?? '',
        containerStatus: data.containerStatus ?? '',
        seals: data.seals ?? [],
        freeStorageDate: data.freeStorageDate ?? '',
        freeRetentionDate: data.freeRetentionDate ?? '',
        vgm: data.vgm ?? 0,
        vgmUoM: data.vgmUoM ?? '',
        volume: data.volume ?? 0,
        volumeUoM: data.volumeUoM ?? '',
        palletCount: data.palletCount ?? 0,
        requiresTemperature: data.requiresTemperature ?? false,
        isDangerous: data.isDangerous ?? false,
        undg: data.undg,
        imoCode: data.imoCode,
        flashpoint: data.flashpoint,
        flashpointUoM: data.flashpointUoM,
        minTemp: data.minTemp,
        maxTemp: data.maxTemp,
        tempUoM: data.tempUoM,
        invoice: data.invoice ?? [],
        storageIncurredDays: data.storageIncurredDays ?? 0,
        storageCostPerDay: data.storageCostPerDay ?? 0,
        storageCostCurrency: data.storageCostCurrency ?? 'USD',
        storageIncurredCost: data.storageIncurredCost ?? 0,
        retentionIncurredDays: data.retentionIncurredDays ?? 0,
        retentionCostPerDay: data.retentionCostPerDay ?? 0,
        retentionCostCurrency: data.retentionCostCurrency ?? 'USD',
        retentionIncurredCost: data.retentionIncurredCost ?? 0,
        portReturnDate: data.portReturnDate ?? '',
        portPickupDate: data.portPickupDate ?? '',
        licensePlate: data.licensePlate ?? '',
        driverName: data.driverName ?? '',
        driverIdentification: data.driverIdentification ?? '',
        loadingAppointmentDate: data.loadingAppointmentDate ?? '',
        warehouseArrivalDate: data.warehouseArrivalDate ?? '',
        unloadingDate: data.unloadingDate ?? '',
        unitOfVolume: data.unitOfVolume ?? '',
        unitOfWeight: data.unitOfWeight ?? '',
        pinNumber: data.pinNumber ?? '',
        approvalDateTime: data.approvalDateTime ?? '',
        transportCompany: data.transportCompany ?? '',
        bookingNumber: data.bookingNumber ?? '',
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date())
      };
      return container;
    });
  }

  /**
   * Calcula automáticamente los días y costos incurridos basándose en las fechas del contenedor
   */
  private calculateContainerCosts(containerData: Partial<IContainer>): Partial<IContainer> {
    const result = { ...containerData };

    // Calcular días y costos de almacenamiento
    if (containerData.freeStorageDate && containerData.portPickupDate) {
      const freeDate = new Date(containerData.freeStorageDate);
      const pickupDate = new Date(containerData.portPickupDate);
      
      freeDate.setHours(0, 0, 0, 0);
      pickupDate.setHours(0, 0, 0, 0);
      
      if (freeDate >= pickupDate) {
        result.storageIncurredDays = 0;
      } else {
        const diffTime = pickupDate.getTime() - freeDate.getTime();
        result.storageIncurredDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      }
      
      // Calcular costo total de almacenaje
      const costPerDay = containerData.storageCostPerDay || 0;
      result.storageIncurredCost = Number((result.storageIncurredDays * costPerDay).toFixed(2));
      
    }

    // Calcular días y costos de retención (demora)
    if (containerData.freeRetentionDate && containerData.portReturnDate) {
      const freeDate = new Date(containerData.freeRetentionDate);
      const returnDate = new Date(containerData.portReturnDate);
      
      freeDate.setHours(0, 0, 0, 0);
      returnDate.setHours(0, 0, 0, 0);
      
      if (freeDate >= returnDate) {
        result.retentionIncurredDays = 0;
      } else {
        const diffTime = returnDate.getTime() - freeDate.getTime();
        result.retentionIncurredDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      }
      
      // Calcular costo total de demora
      const costPerDay = containerData.retentionCostPerDay || 0;
      result.retentionIncurredCost = Number((result.retentionIncurredDays * costPerDay).toFixed(2));
      
    }

    return result;
  }

  async addContainer(
    shipmentId: string, 
    containerData: Omit<IContainer, 'id'>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    isImported?: boolean
  ): Promise<string> {

    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }

    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const containersCollectionRef = collection(shipmentRef, 'containers')
    const newContainerRef = doc(containersCollectionRef)
    

    const batch = writeBatch(this.db);

    // Calcular automáticamente los costos basados en las fechas
    const containerWithCalculations = this.calculateContainerCosts(containerData);

    // Preparar los datos del contenedor con timestamps
    const now = Timestamp.now();
    const containerToSave = {
      ...containerWithCalculations,
      id: newContainerRef.id,
      createdAt: now,
      updatedAt: now
    }
    

    // Agregar el contenedor
    batch.set(newContainerRef, containerToSave)

    // Si hay información del usuario, registrar en el historial de cambios
    if (userId && name && lastName) {

      // Actualizar el documento principal del shipment
      const shipmentUpdate = {
        updatedAt: now,
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      };
      batch.update(shipmentRef, shipmentUpdate);

      // Registrar en el historial de cambios
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');

      // Función auxiliar para aplanar objetos anidados
      const flattenObject = (obj: any, prefix = '') => {
        return Object.keys(obj).reduce((acc: any, key: string) => {
          const newKey = prefix ? `${prefix}.${key}` : key;
          if (typeof obj[key] === 'object' && obj[key] !== null && !(obj[key] instanceof Date) && !Array.isArray(obj[key])) {
            Object.assign(acc, flattenObject(obj[key], newKey));
          } else {
            acc[newKey] = obj[key];
          }
          return acc;
        }, {});
      };

      // Aplanar el objeto del contenedor
      const flatContainer = flattenObject(containerToSave);

      // Crear los registros de cambio
      const changes = Object.entries(flatContainer)
        .filter(([field, value]) => {
          // Excluir campos internos y timestamps
          if (field === 'id' || field === 'createdAt' || field === 'updatedAt') {
            return false;
          }

          // Excluir valores nulos, undefined o vacíos
          if (value === null || value === undefined || value === '') {
            return false;
          }

          // Excluir booleanos false (valores por defecto)
          if (value === false) {
            return false;
          }

          // Excluir campos de unidades de medida con valores por defecto
          if (field.toLowerCase().includes('uom') && ['°C', 'CM', 'KGM'].includes(value as string)) {
            return false;
          }

          // Excluir números que son 0 o negativos
          if (typeof value === 'number' && value <= 0) {
            return false;
          }

          return true;
        })
        .map(([field, value]) => ({
          field: `container.${newContainerRef.id}.${field}`,
          oldValue: null,
          newValue: value
        }));


      const changeRecord: ShipmentChange = {
        timestamp: now,        
        userId,
        name,
        lastName,
        changes,
        action: 'CREATE',
        source: isImported ? 'import data' : 'manual',
        description: containerData.number 
          ? isImported 
            ? `Importó el contenedor ${containerData.number}`
            : `Creó un contenedor con número ${containerData.number}`
          : 'Creó un contenedor sin número'
      };

      const changeDocRef = doc(changesCollectionRef);
      batch.set(changeDocRef, changeRecord);
    } else {
      console.warn('⚠️ No se registrarán cambios porque falta información del usuario');
    }

    try {
      await batch.commit();
    } catch (error) {
      console.error('❌ Error en batch commit:', error);
      throw error;
    }

    return newContainerRef.id;
  }

  async updateContainer(
    shipmentId: string, 
    containerId: string, 
    containerData: Partial<IContainer>, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !containerId || !companyId) {
      throw new Error('Shipment ID, Container ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const containerRef = doc(collection(shipmentRef, 'containers'), containerId)
    
    try {
      // 1. Obtener el contenedor actual antes de la actualización
      const currentDoc = await getDoc(containerRef)
      if (!currentDoc.exists()) {
        throw new Error('Container not found')
      }
      const currentData = currentDoc.data()

      // 1.5. Combinar datos actuales con nuevos para tener el contexto completo para cálculos
      const mergedData = { ...currentData, ...containerData };
      
      // Calcular automáticamente los costos basados en las fechas (actuales + nuevas)
      const containerWithCalculations = this.calculateContainerCosts(mergedData);

      // 2. Realizar la actualización del contenedor
      const cleanedData = this.cleanObject(containerWithCalculations)
      
      // Preservar unitOfWeight y unitOfVolume incluso si son strings vacíos
      const updateData: any = {
        ...cleanedData,
        updatedAt: serverTimestamp()
      }
      
      // Si estos campos existen en containerData, preservarlos
      if ('unitOfWeight' in containerData) {
        updateData.unitOfWeight = containerData.unitOfWeight || ''
      }
      if ('unitOfVolume' in containerData) {
        updateData.unitOfVolume = containerData.unitOfVolume || ''
      }
      
      await updateDoc(containerRef, updateData)

      // 3. Actualizar el documento principal del shipment para disparar las notificaciones
      if (userId && name && lastName) {
        await updateDoc(shipmentRef, {
          updatedAt: serverTimestamp()
        })

        // 4. Registrar los cambios en la subcolección changesHistory del shipment
        // Filtrar solo los campos que realmente cambiaron
        let changes = Object.keys(containerData)
          .map(field => {
            const oldValue = currentData[field]
            const newValue = containerData[field]

            // Si los valores son iguales después de limpiarlos, retornar null
            if (this.areValuesEqual(oldValue, newValue)) {
              return null
            }

            // Limpiar los valores antes de guardarlos en el historial
            return {
              field: `container.${containerId}.${field}`,
              oldValue: this.cleanObject(oldValue),
              newValue: this.cleanObject(newValue)
            }
          })
          .filter((change): change is NonNullable<typeof change> => 
            change !== null && 
            !this.areValuesEqual(change.oldValue, change.newValue)
          )

        if (changes.length > 0) {
          const changeRecord: ShipmentChange = {
            timestamp: new Date(),
            userId,
            name,
            lastName,
            changes,
            action: 'UPDATE',
            description: currentData.number 
              ? `Actualizó el contenedor con número ${currentData.number}`
              : 'Actualizó un contenedor sin número'
          }

          const changesCollectionRef = collection(shipmentRef, 'changesHistory')
          await addDoc(changesCollectionRef, {
            ...changeRecord,
            timestamp: Timestamp.fromDate(changeRecord.timestamp)
          })
        }
      }
    } catch (error) {
      throw error
    }
  }

  async deleteContainer(
    shipmentId: string, 
    containerId: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void> {
    if (!shipmentId || !containerId || !companyId) {
      throw new Error('Shipment ID, Container ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const containerRef = doc(collection(shipmentRef, 'containers'), containerId)
    
    // Get container data before deletion
    const containerSnap = await getDoc(containerRef);
    if (!containerSnap.exists()) {
      throw new Error('Container not found');
    }
    const containerData = containerSnap.data();

    // Delete the container
    await deleteDoc(containerRef);

    // Record the change in history if user info is provided
    if (userId && name && lastName) {
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      const changeData: ShipmentChange = {
        timestamp: new Date(),
        userId,
        name,
        lastName,
        action: 'DELETE',
        description: description || (containerData.number 
          ? `Eliminó el contenedor ${containerData.number}`
          : 'Eliminó un contenedor sin número'),
        changes: [{
          field: `container.${containerId}`,
          oldValue: this.cleanObject(containerData),
          newValue: null
        }]
      };

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      });

      // Update main shipment document to trigger notifications
      await updateDoc(shipmentRef, {
        lastUpdate: serverTimestamp(),
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });
    }
  }

  async addDeletedContainer(
    shipmentId: string, 
    containerNumber: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !containerNumber || !companyId) {
      throw new Error('Shipment ID, Container Number, and Company ID are required')
    }
    
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    
    // Get current shipment data
    const shipmentSnap = await getDoc(shipmentRef);
    if (!shipmentSnap.exists()) {
      throw new Error('Shipment not found');
    }
    
    const shipmentData = shipmentSnap.data();
    const currentDeletedContainers = shipmentData.containersDeleted || [];
    
    // Check if container is already in the deleted list
    if (currentDeletedContainers.includes(containerNumber)) {
      return;
    }
    
    // Add container number to deleted list
    const updatedDeletedContainers = [...currentDeletedContainers, containerNumber];
    
    // Update shipment document
    await updateDoc(shipmentRef, {
      containersDeleted: updatedDeletedContainers,
      lastUpdate: serverTimestamp(),
      updatedBy: userId && name && lastName ? {
        id: userId,
        name,
        lastName
      } : shipmentData.updatedBy
    });
    
    // Record the change in history if user info is provided
    if (userId && name && lastName) {
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      const changeData: ShipmentChange = {
        timestamp: new Date(),
        userId,
        name,
        lastName,
        action: 'UPDATE',
        description: `Marcó el contenedor ${containerNumber} como eliminado para prevenir recreación automática`,
        changes: [{
          field: 'containersDeleted',
          oldValue: currentDeletedContainers,
          newValue: updatedDeletedContainers
        }]
      };

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      });
    }
  }

  // Products methods
  async getProducts(shipmentId: string, companyId?: string): Promise<any[]> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const itemsCollectionRef = collection(shipmentRef, 'items')
    const itemsSnapshot = await getDocs(itemsCollectionRef)
    
    return itemsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
        confirmedDate: data.confirmedDate ? (typeof data.confirmedDate === 'string' ? new Date(data.confirmedDate) : (data.confirmedDate?.toDate ? data.confirmedDate.toDate() : data.confirmedDate)) : null
      }
    })
  }

  async addProduct(
    shipmentId: string, 
    productData: any, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<string> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const itemsCollectionRef = collection(shipmentRef, 'items')
    
    // Validar si el SKU ya existe
    if (productData.sku) {
      const existingItemsSnapshot = await getDocs(itemsCollectionRef);
      const skuExists = existingItemsSnapshot.docs.some(doc => 
        doc.data().sku?.toLowerCase() === productData.sku.toLowerCase()
      );
      
      if (skuExists) {
        throw new Error(`El SKU "${productData.sku}" ya existe en este embarque`);
      }
    }
    
    const newProductRef = doc(itemsCollectionRef)
    
    const batch = writeBatch(this.db);

    const now = Timestamp.now();
    
    // Convertir confirmedDate a Timestamp si es string
    let confirmedDateTimestamp = null;
    if (productData.confirmedDate) {
      if (typeof productData.confirmedDate === 'string') {
        confirmedDateTimestamp = Timestamp.fromDate(new Date(productData.confirmedDate));
      } else if (productData.confirmedDate instanceof Date) {
        confirmedDateTimestamp = Timestamp.fromDate(productData.confirmedDate);
      } else {
        confirmedDateTimestamp = productData.confirmedDate;
      }
    }
    
    // Limpiar el productData antes de guardar
    const cleanedData = this.cleanObject(productData);
    
    const productToSave = {
      ...cleanedData,
      confirmedDate: confirmedDateTimestamp,
      id: newProductRef.id,
      createdAt: now,
      updatedAt: now
    };

    batch.set(newProductRef, productToSave);

    if (userId && name && lastName) {
      batch.update(shipmentRef, {
        updatedAt: now,
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });

      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      const changeData = {
        timestamp: now,
        type: 'PRODUCT_ADDED',
        userId,
        name,
        lastName,
        action: 'CREATE',
        description: productData.sku 
          ? `Agregó el producto con SKU ${productData.sku}`
          : 'Agregó un producto',
        changes: [{
          field: `product.${newProductRef.id}`,
          oldValue: null,
          newValue: this.cleanObject(productData)
        }]
      };

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      });
    }

    await batch.commit();
    return newProductRef.id;
  }

  async updateProduct(
    shipmentId: string, 
    productId: string, 
    productData: any, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !productId || !companyId) {
      throw new Error('Shipment ID, Product ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const itemsCollectionRef = collection(shipmentRef, 'items')
    const productRef = doc(itemsCollectionRef, productId)
    
    const productSnap = await getDoc(productRef);
    if (!productSnap.exists()) {
      throw new Error('Product not found');
    }
    const oldProductData = productSnap.data();
    
    // Validar SKU duplicado solo si cambió el SKU
    if (productData.sku && productData.sku !== oldProductData.sku) {
      const existingItemsSnapshot = await getDocs(itemsCollectionRef);
      const skuExists = existingItemsSnapshot.docs.some(doc => 
        doc.id !== productId && doc.data().sku?.toLowerCase() === productData.sku.toLowerCase()
      );
      
      if (skuExists) {
        throw new Error(`El SKU "${productData.sku}" ya existe en este embarque`);
      }
    }

    const batch = writeBatch(this.db);
    const now = Timestamp.now();
    
    // Convertir confirmedDate a Timestamp si es string
    let confirmedDateTimestamp = productData.confirmedDate;
    if (productData.confirmedDate) {
      if (typeof productData.confirmedDate === 'string') {
        confirmedDateTimestamp = Timestamp.fromDate(new Date(productData.confirmedDate));
      } else if (productData.confirmedDate instanceof Date) {
        confirmedDateTimestamp = Timestamp.fromDate(productData.confirmedDate);
      }
    }
    
    // Limpiar el productData antes de guardar
    const cleanedData = this.cleanObject(productData);

    batch.update(productRef, {
      ...cleanedData,
      confirmedDate: confirmedDateTimestamp,
      updatedAt: now
    });

    if (userId && name && lastName) {
      batch.update(shipmentRef, {
        updatedAt: now,
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });

      // Registrar los cambios en la subcolección changesHistory
      const changes = Object.keys(productData)
        .map(field => {
          const oldValue = oldProductData[field]
          const newValue = productData[field]

          // Si los valores son iguales o ambos undefined/null, retornar null
          if (this.areValuesEqual(oldValue, newValue)) {
            return null
          }

          // Limpiar undefined y null
          const cleanOldValue = oldValue === undefined || oldValue === null ? null : this.cleanObject(oldValue)
          const cleanNewValue = newValue === undefined || newValue === null ? null : this.cleanObject(newValue)

          return {
            field: `product.${productId}.${field}`,
            oldValue: cleanOldValue,
            newValue: cleanNewValue
          }
        })
        .filter((change): change is NonNullable<typeof change> => 
          change !== null && 
          !this.areValuesEqual(change.oldValue, change.newValue)
        )

      if (changes.length > 0) {
        const changesCollectionRef = collection(shipmentRef, 'changesHistory');
        const changeData: any = {
          timestamp: Timestamp.fromDate(new Date()),
          type: 'PRODUCT_UPDATED',
          userId,
          name,
          lastName,
          action: 'UPDATE',
          description: productData.sku 
            ? `Actualizó el producto con SKU ${productData.sku}`
            : 'Actualizó un producto',
          changes
        };

        // Limpiar cualquier undefined del changeData
        Object.keys(changeData).forEach(key => {
          if (changeData[key] === undefined) {
            delete changeData[key];
          }
        });

        await addDoc(changesCollectionRef, changeData);
      }
    }

    await batch.commit();
  }

  async deleteProduct(
    shipmentId: string, 
    productId: string, 
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void> {
    if (!shipmentId || !productId || !companyId) {
      throw new Error('Shipment ID, Product ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const productRef = doc(collection(shipmentRef, 'items'), productId)
    
    const productSnap = await getDoc(productRef);
    if (!productSnap.exists()) {
      throw new Error('Product not found');
    }
    const productData = productSnap.data();

    await deleteDoc(productRef);

    if (userId && name && lastName) {
      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      const changeData: ShipmentChange = {
        timestamp: new Date(),
        userId,
        name,
        lastName,
        action: 'DELETE',
        description: description || (productData.sku 
          ? `Eliminó el producto con SKU ${productData.sku}`
          : 'Eliminó un producto'),
        changes: [{
          field: `product.${productId}`,
          oldValue: this.cleanObject(productData),
          newValue: null
        }]
      };

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      });

      await updateDoc(shipmentRef, {
        lastUpdate: serverTimestamp(),
        updatedBy: {
          id: userId,
          name,
          lastName
        }
      });
    }
  }

  async listenToProducts(
    shipmentId: string, 
    callback: (products: any[]) => void,
    companyId?: string
  ): Promise<() => void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const itemsCollectionRef = collection(shipmentRef, 'items')
    
    return onSnapshot(itemsCollectionRef, (snapshot) => {
      const products = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
          confirmedDate: data.confirmedDate ? (typeof data.confirmedDate === 'string' ? new Date(data.confirmedDate) : (data.confirmedDate?.toDate ? data.confirmedDate.toDate() : data.confirmedDate)) : null
        }
      });
      callback(products);
    });
  }

  // Métodos para usuarios activos
  private getActiveUsersRef(shipmentId: string, companyId?: string) {
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
    return collection(shipmentRef, 'activeUsers');
  }

  async registerActiveUser(
    shipmentId: string,
    userId: string,
    userName: string,
    companyId?: string,
    lastName?: string
  ) {
    try {
      const activeUsersRef = this.getActiveUsersRef(shipmentId, companyId);
      const userDocRef = doc(activeUsersRef, userId);
      
      // Verificar si el documento ya existe
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Primera vez - crear documento con todos los campos
        const newUserData = {
          userId,
          userName,
          lastName,
          lastSeen: serverTimestamp(),
          createdAt: serverTimestamp(),
          isActive: true
        };
        await setDoc(userDocRef, newUserData);
      } else {
        // Usuario existente - actualizar campos
        const updateData = {
          userName,
          lastName,
          lastSeen: serverTimestamp(),
          isActive: true
        };
        await updateDoc(userDocRef, updateData);
      }
    } catch (error) {
      throw error;
    }
  }

  async removeActiveUser(shipmentId: string, userId: string, companyId?: string) {
    
    try {
      const activeUsersRef = this.getActiveUsersRef(shipmentId, companyId);
      const userDocRef = doc(activeUsersRef, userId);
      
      // Verificar estado inicial
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        return;
      }

      // Preparar datos de actualización
      const updateData = {
        lastSeen: serverTimestamp(),
        isActive: false
      };
      
      // Actualizar el documento
      await updateDoc(userDocRef, updateData);
      
      // Verificar que se actualizó correctamente
      const updatedDoc = await getDoc(userDocRef);
      const updatedData = updatedDoc.data();

      if (updatedData?.isActive === true) {
        throw new Error('¡ADVERTENCIA! El documento aún tiene isActive=true después de la actualización');
      }
    } catch (error) {
      throw error
    }
  }

  subscribeToActiveUsers(
    shipmentId: string,
    callback: (users: Array<{
      userId: string
      userName: string
      lastName?: string
      role?: string
      lastSeen: Date
      createdAt: Date
      isActive: boolean
    }>) => void,
    companyId?: string
  ): () => void {
    const activeUsersRef = this.getActiveUsersRef(shipmentId, companyId);
    const q = query(activeUsersRef, where('isActive', '==', true));

    return onSnapshot(q, async (snapshot) => {
      const activeUsersPromises = snapshot.docs.map(async (docSnapshot) => {
        const data = docSnapshot.data();
        // Corregir la referencia al documento de usuario
        const userDocRef = doc(this.db, 'users', data.userId);
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.data() as UserDocument | undefined;
        
        return {
          userId: data.userId,
          userName: data.userName,
          lastName: data.lastName,
          role: userData?.role || '',
          lastSeen: data.lastSeen?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          isActive: data.isActive
        };
      });

      const activeUsers = await Promise.all(activeUsersPromises);
      callback(activeUsers);
    }, error => {
      throw error;
    });
  }

  private cleanObject(obj: any): any {
    // Convertir undefined a null
    if (obj === undefined) {
      return null;
    }
    
    // Convertir strings vacíos a null
    if (obj === '') {
      return null;
    }
    
    // Si es null, retornarlo tal cual
    if (obj === null) {
      return null;
    }
    
    // Si es un array, limpiar cada elemento
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanObject(item));
    }

    // Si es un objeto, limpiar cada propiedad recursivamente
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        cleaned[key] = this.cleanObject(value);
      }
      return cleaned;
    }

    // Para valores primitivos (string, number, boolean), retornar tal cual
    return obj;
  }

  private areValuesEqual(val1: any, val2: any): boolean {
    // Limpiar los valores antes de comparar
    const cleanVal1 = this.cleanObject(val1);
    const cleanVal2 = this.cleanObject(val2);

    // Si después de limpiar, ambos son null o undefined, son iguales
    if (!cleanVal1 && !cleanVal2) return true;
    
    // Si uno es null/undefined y el otro no, son diferentes
    if (!cleanVal1 || !cleanVal2) return false;

    // Si ambos son arrays
    if (Array.isArray(cleanVal1) && Array.isArray(cleanVal2)) {
      if (cleanVal1.length !== cleanVal2.length) return false;
      return JSON.stringify(cleanVal1) === JSON.stringify(cleanVal2);
    }

    // Si ambos son objetos
    if (typeof cleanVal1 === 'object' && typeof cleanVal2 === 'object') {
      return JSON.stringify(cleanVal1) === JSON.stringify(cleanVal2);
    }

    // Para valores primitivos
    return cleanVal1 === cleanVal2;
  }

  async importShipments(
    shipments: Partial<Shipment>[], 
    file: File,
    companyId?: string, 
    userId?: string
  ): Promise<Shipment[]> {
    if (!companyId) {
      throw new Error('Company ID is required for importing shipments');
    }

    if (!userId) {
      throw new Error('User ID is required for importing shipments');
    }

    const importedShipments: Shipment[] = [];

    try {
      // 0. Almacenar el archivo de importación y obtener su URL
      const { url } = await this.storeImportFile(file, userId);
      
      // 1. Obtener todos los contadores necesarios de una sola vez
      const allCounters = await this.getAndIncrementCounterBatch(companyId!, shipments.length);
      
      // 2. Dividir en lotes para las escrituras
      const BATCH_SIZE = 500; // Límite de Firestore para escrituras en batch
      const batches: Partial<Shipment>[][] = [];
      
      for (let i = 0; i < shipments.length; i += BATCH_SIZE) {
        batches.push(shipments.slice(i, i + BATCH_SIZE));
      }

      // 3. Procesar los lotes en paralelo
      const batchResults = await Promise.all(
        batches.map(async (batchShipments, batchIndex) => {
          const writeBatchRef = writeBatch(this.db);
          const batchResults: Shipment[] = [];

          batchShipments.forEach((shipmentData, index) => {
            const globalIndex = batchIndex * BATCH_SIZE + index;
            const counter = allCounters[globalIndex];
            
            const docRef = doc(this.getCollectionRef(companyId!));
            const newShipment = {
              ...shipmentData,
              id: docRef.id,
              liberationNumber: counter.toString().padStart(8, '0'),
              idNumber: `SHIP-${counter.toString().padStart(7, '0')}`,
              createdAt: new Date(),
              isImported: true
            };

            writeBatchRef.set(docRef, this.toFirestore(newShipment));
            batchResults.push(newShipment as Shipment);
          });

          await writeBatchRef.commit();
          return batchResults;
        })
      );

      // Consolidar resultados
      batchResults.forEach(results => {
        importedShipments.push(...results);
      });

      // Actualizar el usage history una sola vez al final
      if (userId) {
        const usageHistoryRef = collection(this.db, 'Users', userId, 'usageHistory');
        const q = query(usageHistoryRef, where('createdAt', '!=', null));
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(this.db);

        if (querySnapshot.size > 0) {
          const mostRecentDoc = querySnapshot.docs[0];
          batch.update(mostRecentDoc.ref, {
            shipmentUsage: (mostRecentDoc.data().shipmentUsage || 0) + shipments.length,
            lastUpdate: serverTimestamp()
          });
        } else {
          const newUsageRef = doc(usageHistoryRef);
          batch.set(newUsageRef, {
            shipmentUsage: shipments.length,
            createdAt: serverTimestamp(),
            lastUpdate: serverTimestamp()
          });
        }

        // Registrar la actividad de importación
        const userActivityRef = doc(collection(this.db, 'Users', userId, 'activities'));
        batch.set(userActivityRef, {
          type: 'IMPORT_SHIPMENTS',
          count: shipments.length,
          fileUrl: url,
          fileName: file.name,
          timestamp: serverTimestamp(),
          companyId
        });

        await batch.commit();
      }

      return importedShipments;
    } catch (error) {
      console.error('Error importing shipments:', error);
      throw new Error(`Failed to import shipments: ${(error as Error).message}`);
    }
  }

  private async storeImportFile(file: File, userId: string): Promise<{ url: string; isNew: boolean }> {
    try {
      const storage = getStorage();
      const fileHash = await this.calculateFileHash(file);
      const fileExtension = file.name.split('.').pop();
      const fileName = `${fileHash}.${fileExtension}`;
      const filePath = `importData/users/${userId}/files/${fileName}`;

      // Verificar si el archivo ya existe
      const userFilesRef = storageRef(storage, `importData/users/${userId}/files`);
      const existingFiles = await listAll(userFilesRef);
      const existingFile = existingFiles.items.find(item => item.name === fileName);

      if (existingFile) {
        const downloadUrl = await getDownloadURL(existingFile);
        return { url: downloadUrl, isNew: false };
      }

      // Subir archivo nuevo
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);

      return { url: downloadUrl, isNew: true };
    } catch (error) {
      console.error('❌ Error al guardar archivo:', error);
      throw new Error(`Error al guardar archivo: ${(error as Error).message}`);
    }
  }

  private async calculateFileHash(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async saveImportMapping(mapping: Omit<ImportMapping, 'id'>): Promise<string> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore();
      const mappingCollection = collection(db, 'Users', mapping.userId, 'importMappings');
      
      // Verificar si ya existe un mapeo con el mismo hash
      const existingMapping = await this.getImportMappingByHash(mapping.userId, mapping.fileHash);
      
      if (existingMapping) {
        await updateDoc(doc(mappingCollection, existingMapping.id), {
          ...mapping,
          updatedAt: serverTimestamp()
        });
        return existingMapping.id;
      }
      
      const docRef = await addDoc(mappingCollection, {
        ...mapping,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error al guardar mapeo:', error);
      throw new Error(`Error al guardar mapeo: ${(error as Error).message}`);
    }
  }

  async getImportMappingByHash(userId: string, fileHash: string): Promise<ImportMapping | null> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore();
      const mappingCollection = collection(db, 'Users', userId, 'importMappings');
      
      const q = query(mappingCollection, where('fileHash', '==', fileHash));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as ImportMapping;
    } catch (error) {
      console.error('Error al obtener mapeo:', error);
      throw new Error(`Error al obtener mapeo: ${(error as Error).message}`);
    }
  }

  async updateImportMapping(id: string, mapping: Partial<ImportMapping>): Promise<void> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore();
      const docRef = doc(db, 'Users', mapping.userId!, 'importMappings', id);
      
      await updateDoc(docRef, {
        ...mapping,
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error al actualizar mapeo:', error);
      throw new Error(`Error al actualizar mapeo: ${(error as Error).message}`);
    }
  }

  async listImportMappings(userId: string): Promise<ImportMapping[]> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore();
      const mappingCollection = collection(db, 'Users', userId, 'importMappings');
      
      const querySnapshot = await getDocs(mappingCollection);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ImportMapping[];
    } catch (error) {
      console.error('❌ Error al listar mapeos:', error);
      throw new Error(`Error al listar mapeos: ${(error as Error).message}`);
    }
  }

  async checkFileExists(userId: string, fileHash: string): Promise<boolean> {
    try {
      const storage = getStorage();
      const userFilesRef = storageRef(storage, `importData/users/${userId}/files`);
      const existingFiles = await listAll(userFilesRef);
      
      // Primero buscar archivos que empiecen con el hash (sistema anterior)
      let existingFile = existingFiles.items.find(item => 
        item.name.startsWith(fileHash)
      );

      // Si no se encuentra por hash, intentar buscar por identificador de nombre+usuario
      if (!existingFile) {
        // Buscar archivos que contengan el userId en el nombre (nuevo sistema)
        existingFile = existingFiles.items.find(item => 
          item.name.includes(userId)
        );
      }

      return !!existingFile;
    } catch (error) {
      console.error('❌ Error al verificar archivo:', error);
      throw error;
    }
  }

  async getFileUrl(userId: string, fileHash: string): Promise<string> {
    try {
      const storage = getStorage();
      const userFilesRef = storageRef(storage, `importData/users/${userId}/files`);
      
      // Listar todos los archivos del usuario
      const existingFiles = await listAll(userFilesRef);
      
      // Primero buscar el archivo que empiece con el hash (sistema anterior)
      let existingFile = existingFiles.items.find(item => 
        item.name.startsWith(fileHash)
      );

      // Si no se encuentra por hash, intentar buscar por identificador de nombre+usuario
      if (!existingFile) {
        // Buscar archivos que contengan el userId en el nombre (nuevo sistema)
        existingFile = existingFiles.items.find(item => 
          item.name.includes(userId)
        );
      }

      if (!existingFile) {
        throw new Error('Archivo no encontrado');
      }

      return await getDownloadURL(existingFile);
    } catch (error) {
      console.error('❌ Error al obtener URL del archivo:', error);
      throw error;
    }
  }

  async storeFile(userId: string, file: File, fileHash: string): Promise<string> {
    try {
      const storage = getStorage()
      const fileExtension = file.name.split('.').pop()
      const fileName = `${fileHash}.${fileExtension}`
      const filePath = `importData/users/${userId}/files/${fileName}`
      const fileRef = storageRef(storage, filePath)

      // Subir el archivo
      await uploadBytes(fileRef, file)

      // Obtener la URL del archivo subido
      return await getDownloadURL(fileRef)
    } catch (error) {
      console.error('❌ Error al almacenar archivo:', error)
      throw error
    }
  }

  async importShipmentsBatch(
    shipments: Partial<Shipment>[], // eslint-disable-next-line @typescript-eslint/no-explicit-any
    companyId: string
  ): Promise<Shipment[]> {
    if (!companyId) {
      throw new Error('Company ID is required for importing shipments');
    }

    try {
      // Obtener contadores en batch
      const counters = await this.getAndIncrementCounterBatch(companyId, shipments.length);
      
      // Crear un timestamp base para toda la importación
      const baseTimestamp = new Date();
      
      // Crear referencias de documentos y preparar datos
      const batchOperations = shipments.map((shipment, index) => {
        const docRef = doc(this.getCollectionRef(companyId));
        const counter = counters[index];
        
        // Crear un timestamp único agregando milisegundos basados en el índice
        const creationDate = new Date(baseTimestamp.getTime() + index);
        
        return {
          ref: docRef,
          data: {
            ...shipment,
            id: docRef.id,
            liberationNumber: counter.toString().padStart(8, '0'),
            idNumber: `SHIP-${counter.toString().padStart(7, '0')}`,
            createdAt: creationDate,
            updatedAt: creationDate,
            isImported: true
          }
        };
      });

      // Ejecutar escrituras en batch
      const batches: Shipment[] = [];
      const FIRESTORE_BATCH_LIMIT = 500;

      for (let i = 0; i < batchOperations.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(this.db);
        const currentBatch = batchOperations.slice(i, i + FIRESTORE_BATCH_LIMIT);

        currentBatch.forEach(({ ref, data }) => {
          batch.set(ref, this.toFirestore(data));
          batches.push(data as Shipment);
        });

        await batch.commit();
      }

      return batches;
    } catch (error) {
      if (error instanceof ShipmentLimitReachedError) throw error;
      console.error('Error in batch import:', error);
      throw new Error(`Failed to import shipments batch: ${error.message}`);
    }
  }
  
  async getNotifications(shipmentId: string, companyId: string, userId: string): Promise<INotification[]> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required');
    }

    try {
      const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
      const notificationsRef = collection(shipmentRef, "notifications");
      const notificationsQuery = query(notificationsRef);
      const querySnapshot = await getDocs(notificationsQuery);

      return querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((data) => {
          const isUserIncluded = data.usersNotification?.includes(userId);
          return isUserIncluded;
        })
        .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()) as INotification[];
    } catch (error) {
      console.error("Error getting notifications:", error);
      return [];
    }
  }

  async addShipmentChangeRecord(companyId: string, shipmentId: string, changeRecord: ShipmentChange): Promise<void> {
    const changesCollectionRef = collection(this.getCollectionRef(companyId), shipmentId, 'changesHistory');
    await addDoc(changesCollectionRef, {
        ...changeRecord,
        timestamp: Timestamp.fromDate(changeRecord.timestamp)
    });
  }

  async addComment(
    shipmentId: string,
    commentData: {
      comment: string;
      fullName: string;
      createdAt: Date;
      userId: string;
    },
    companyId?: string
  ): Promise<string> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required');
    }
  
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
    
    try {
      // Generar un ID único para el comentario
      const commentId = doc(collection(this.db, 'temp')).id;
      
      // Preparar los datos del comentario para Firestore
      const commentToSave = {
        id: commentId,
        ...commentData,
        createdAt: Timestamp.fromDate(commentData.createdAt)
      };
      
      // Obtener el documento actual para registrar el cambio
      const currentDoc = await getDoc(shipmentRef);
      
      // Actualizar el documento del shipment añadiendo el comentario al array
      await updateDoc(shipmentRef, {
        additionalComments: arrayUnion(commentToSave),
        lastUpdate: serverTimestamp()
      });
      
      // Registrar el cambio en changesHistory
      const changeRecord: ShipmentChange = {
        timestamp: new Date(),
        userId: commentData.userId,
        name: commentData.fullName.split(' ')[0] || '',
        lastName: commentData.fullName.split(' ').slice(1).join(' ') || '',
        changes: [{
          field: 'additionalComments',
          oldValue: null,
          newValue: commentData.comment
        }],
        action: 'CREATE',
        description: `Añadió un comentario: "${commentData.comment.substring(0, 5)}${commentData.comment.length > 5 ? '...' : ''}"`
      };

      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      await addDoc(changesCollectionRef, {
        ...changeRecord,
        timestamp: Timestamp.fromDate(changeRecord.timestamp)
      });
      
      return commentId;
    } catch (error) {
      console.error('Error al añadir comentario:', error);
      throw error;
    }
  }

  async updateComment(
    shipmentId: string,
    commentId: string,
    commentData: {
      comment: string;
    },
    userData: {
      userId: string;
      fullName: string;
    },
    companyId?: string
  ): Promise<void> {
    if (!shipmentId || !commentId || !companyId) {
      throw new Error('Shipment ID, Comment ID, and Company ID are required');
    }
  
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
    let oldCommentText = '';
  
    try {
      // Realizamos la actualización en una transacción para garantizar atomicidad
      await runTransaction(this.db, async (transaction) => {
        const shipmentDoc = await transaction.get(shipmentRef);
        
        if (!shipmentDoc.exists()) {
          throw new Error('Shipment no encontrado');
        }
        
        const shipmentData = shipmentDoc.data();
        const additionalComments = shipmentData.additionalComments || [];
        
        // Encontrar el comentario que queremos actualizar
        const commentIndex = additionalComments.findIndex((comment: any) => comment.id === commentId);
        if (commentIndex === -1) {
          throw new Error('Comentario no encontrado');
        }
        
        // Guardar el texto del comentario anterior para el registro de cambios
        oldCommentText = additionalComments[commentIndex].comment;
        
        // Crear una copia del array de comentarios
        const updatedComments = [...additionalComments];
        
        // Actualizar el comentario específico
        updatedComments[commentIndex] = {
          ...updatedComments[commentIndex],
          comment: commentData.comment,
          updatedAt: Timestamp.now()
        };
        
        // Actualizar todo el array en una sola operación
        transaction.update(shipmentRef, {
          additionalComments: updatedComments,
          lastUpdate: Timestamp.now()
        });
      });
      
      // Registrar el cambio en changesHistory
      const changeRecord: ShipmentChange = {
        timestamp: new Date(),
        userId: userData.userId,
        name: userData.fullName.split(' ')[0] || '',
        lastName: userData.fullName.split(' ').slice(1).join(' ') || '',
        changes: [{
          field: 'additionalComments',
          oldValue: oldCommentText,
          newValue: commentData.comment
        }],
        action: 'UPDATE',
        description: `Editó un comentario de: "${oldCommentText.substring(0, 10)}${oldCommentText.length > 10 ? '...' : ''}" a: "${commentData.comment.substring(0, 10)}${commentData.comment.length > 10 ? '...' : ''}"`
      };

      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      await addDoc(changesCollectionRef, {
        ...changeRecord,
        timestamp: Timestamp.fromDate(changeRecord.timestamp)
      });
      
    } catch (error) {
      console.error('Error al actualizar comentario:', error);
      throw new Error(`Error al actualizar comentario: ${(error as Error).message}`);
    }
  }

  async deleteComment(
    shipmentId: string,
    commentId: string,
    userData: {
      userId: string;
      fullName: string;
    },
    companyId?: string
  ): Promise<void> {
    if (!shipmentId || !commentId || !companyId) {
      throw new Error('Shipment ID, Comment ID, and Company ID are required');
    }
    
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
    let deletedComment = '';
    
    try {
      // Primero obtenemos el documento actual para acceder al array de comentarios
      const shipmentDoc = await getDoc(shipmentRef);
      if (!shipmentDoc.exists()) {
        throw new Error('Shipment no encontrado');
      }
      
      const shipmentData = shipmentDoc.data();
      const additionalComments = shipmentData.additionalComments || [];
      
      // Encontrar el comentario a eliminar para guardar su texto
      const commentToDelete = additionalComments.find((comment: any) => comment.id === commentId);
      if (!commentToDelete) {
        throw new Error('Comentario no encontrado');
      }
      
      deletedComment = commentToDelete.comment;
      
      // Filtrar el array para eliminar el comentario con el ID especificado
      const updatedComments = additionalComments.filter((comment: any) => comment.id !== commentId);
      
      // Actualizar el documento con el nuevo array de comentarios
      await updateDoc(shipmentRef, {
        additionalComments: updatedComments,
        lastUpdate: serverTimestamp()
      });
      
      // Registrar el cambio en changesHistory
      const changeRecord: ShipmentChange = {
        timestamp: new Date(),
        userId: userData.userId,
        name: userData.fullName.split(' ')[0] || '',
        lastName: userData.fullName.split(' ').slice(1).join(' ') || '',
        changes: [{
          field: 'additionalComments',
          oldValue: deletedComment,
          newValue: null
        }],
        action: 'DELETE',
        description: `Eliminó un comentario: "${deletedComment.substring(0, 50)}${deletedComment.length > 50 ? '...' : ''}"`
      };

      const changesCollectionRef = collection(shipmentRef, 'changesHistory');
      await addDoc(changesCollectionRef, {
        ...changeRecord,
        timestamp: Timestamp.fromDate(changeRecord.timestamp)
      });
      
    } catch (error) {
      console.error('Error al eliminar comentario:', error);
      throw new Error(`Error al eliminar comentario: ${(error as Error).message}`);
    }
  }
  
  async listenToShipmentComments(
    shipmentId: string,
    callback: (comments: Array<{
      id: string;
      comment: string;
      fullName: string;
      createdAt: Date;
      userId: string;
    }>) => void,
    companyId?: string
  ): Promise<() => void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required');
    }
    
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId);
    
    try {
      // Establecer el listener en el documento del shipment
      const unsubscribe = onSnapshot(shipmentRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const shipmentData = docSnapshot.data();
          const additionalComments = shipmentData.additionalComments || [];
          
          // Convertir los timestamps de Firestore a objetos Date de JavaScript
          const comments = additionalComments.map((comment: any) => ({
            id: comment.id,
            comment: comment.comment,
            fullName: comment.fullName,
            createdAt: comment.createdAt.toDate(),
            updatedAt: comment.updatedAt ? comment.updatedAt.toDate() : undefined,
            userId: comment.userId
          }));
          
          callback(comments);
        } else {
          callback([]);
        }
      }, (error) => {
        console.error('Error al escuchar comentarios:', error);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error al configurar listener de comentarios:', error);
      throw new Error(`Error al configurar listener de comentarios: ${(error as Error).message}`);
    }
  }

  // ===================================
  // PARTIES MANAGEMENT
  // ===================================

  /**
   * Actualizar una party específica en el shipment
   */
  async updateParty(
    shipmentId: string,
    partyType: 'shipper' | 'consignee' | 'notify' | 'pickup' | 'delivery' | 'customer' | 'vendor',
    partyData: any,
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }

    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)

    try {
      const updateData: any = {
        [partyType]: partyData,
        updatedAt: serverTimestamp(),
      }

      if (userId && name && lastName) {
        updateData.updatedBy = {
          id: userId,
          name: name,
          lastName: lastName,
        }
      }

      await updateDoc(shipmentRef, updateData)

      // Registrar cambio en historial
      if (userId && name && lastName) {
        await this.addShipmentChangeRecord(companyId, shipmentId, {
          timestamp: new Date(),
          userId,
          name,
          lastName,
          description: `Updated ${partyType}`,
          action: 'UPDATE',
          source: 'manual',
          changes: [{
            field: partyType,
            oldValue: null,
            newValue: partyData?.name || partyData?.accountNumber || 'Updated',
          }],
        })
      }

    } catch (error) {
      console.error(`Error updating party ${partyType}:`, error)
      throw error
    }
  }

  /**
   * Copiar información de una party a otra
   * Ejemplo: copiar shipper -> pickup
   */
  async copyParty(
    shipmentId: string,
    fromType: 'shipper' | 'consignee' | 'customer' | 'vendor',
    toType: 'pickup' | 'delivery' | 'notify' | 'customer' | 'vendor',
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }

    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)

    try {
      // Obtener shipment actual
      const shipmentDoc = await getDoc(shipmentRef)
      if (!shipmentDoc.exists()) {
        throw new Error('Shipment not found')
      }

      const shipmentData = shipmentDoc.data()
      const sourceParty = shipmentData[fromType]

      if (!sourceParty) {
        throw new Error(`Source party ${fromType} not found`)
      }

      // Copiar y marcar flag correspondiente
      const copiedParty = {
        ...sourceParty,
        ...(toType === 'pickup' && { useShipperInformation: true }),
        ...(toType === 'delivery' && { useConsigneeInformation: true }),
        ...(toType === 'notify' && { useConsigneeInformation: true }),
      }

      await this.updateParty(
        shipmentId,
        toType,
        copiedParty,
        companyId,
        userId,
        name,
        lastName
      )

    } catch (error) {
      console.error(`Error copying party from ${fromType} to ${toType}:`, error)
      throw error
    }
  }

  /**
   * Resetear/limpiar una party a valores vacíos
   */
  async resetParty(
    shipmentId: string,
    partyType: 'shipper' | 'consignee' | 'notify' | 'pickup' | 'delivery',
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    await this.updateParty(
      shipmentId,
      partyType,
      null,
      companyId,
      userId,
      name,
      lastName
    )

  }

  // ===== FILE MANAGEMENT METHODS =====

  /**
   * Registra en changesHistory la carga o eliminación de un archivo del embarque.
   * No lanza: un fallo al registrar el historial no debe frustrar la operación sobre el archivo.
   */
  private async addFileChangeRecord(
    companyId: string,
    shipmentId: string,
    user: { id: string; name: string; lastName?: string },
    action: 'CREATE' | 'DELETE',
    fileName: string
  ): Promise<void> {
    try {
      const changesCollectionRef = collection(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/changesHistory`
      );
      await addDoc(changesCollectionRef, {
        timestamp: Timestamp.fromDate(new Date()),
        userId: user.id,
        name: user.name || '',
        lastName: user.lastName || '',
        action,
        description: action === 'CREATE'
          ? `Cargó el archivo ${fileName}`
          : `Eliminó el archivo ${fileName}`,
        changes: [{
          field: 'file',
          oldValue: action === 'CREATE' ? null : fileName,
          newValue: action === 'CREATE' ? fileName : null
        }]
      });
    } catch (error) {
      console.error('Error registrando el archivo en el historial de cambios:', error);
    }
  }

  /**
   * Get all files for a shipment
   */
  async getShipmentFiles(shipmentId: string, companyId: string): Promise<ShipmentFile[]> {
    try {
      const filesCollectionRef = collection(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files`
      );
      
      const querySnapshot = await getDocs(query(filesCollectionRef, orderBy('uploadedAt', 'desc')));
      
      const files: ShipmentFile[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        files.push({
          id: doc.id,
          shipmentId: data.shipmentId,
          fileType: data.fileType,
          additionalFileTypes: data.additionalFileTypes || [],
          fileName: data.fileName,
          displayName: data.displayName,
          fileUrl: data.fileUrl,
          storagePath: data.storagePath,
          fileSize: data.fileSize,
          mimeType: resolveMimeType(data.mimeType, data.fileName),
          uploadedAt: data.uploadedAt?.toDate() || new Date(),
          uploadedBy: data.uploadedBy,
          companyId: data.companyId,
          extractedData: data.extractedData,
          additionalExtractedData: data.additionalExtractedData,
          reviewedBy: data.reviewedBy ? {
            uid: data.reviewedBy.uid,
            name: data.reviewedBy.name,
            lastName: data.reviewedBy.lastName,
            reviewedAt: data.reviewedBy.reviewedAt?.toDate?.() || data.reviewedBy.reviewedAt
          } : null,
          folderId: data.folderId || null,
          status: data.status,
          error: data.error,
          undoSummary: data.undoSummary,
          sharepoint: data.sharepoint
            ? {
                ...data.sharepoint,
                lockedAt: data.sharepoint.lockedAt?.toDate?.() ?? null,
                syncedAt: data.sharepoint.syncedAt?.toDate?.() ?? null,
              }
            : undefined,
        });
      });

      return files;
    } catch (error) {
      console.error('Error getting shipment files:', error);
      throw error;
    }
  }

  /**
   * Upload a file for a shipment
   */
  async uploadShipmentFile(
    fileData: CreateShipmentFileDTO,
    onProgress?: (progress: number) => void
  ): Promise<ShipmentFile> {
    try {
      const storage = getStorage();
      const fileName = fileData.fileName;
      const filePath = `${fileData.companyId}/shipmentFiles/${fileData.shipmentId}/${fileName}`;
      const fileRef = storageRef(storage, filePath);

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(fileRef, fileData.file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(progress);
            }
          },
          (error) => {
            console.error('Error uploading file:', error);
            reject(error);
          },
          async () => {
            try {
              // Get download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Create file document in Firestore
              const fileDoc: Omit<ShipmentFile, 'id'> = {
                shipmentId: fileData.shipmentId,
                fileType: fileData.fileType,
                additionalFileTypes: fileData.additionalFileTypes || [],
                fileName: fileData.fileName,
                fileUrl: downloadURL,
                fileSize: fileData.file.size,
                mimeType: fileData.file.type,
                uploadedAt: new Date(),
                uploadedBy: fileData.uploadedBy,
                companyId: fileData.companyId,
                folderId: fileData.folderId || null
              };

              const filesCollectionRef = collection(
                this.db,
                `Companies/${fileData.companyId}/shipments/${fileData.shipmentId}/files`
              );
              
              const docRef = await addDoc(filesCollectionRef, {
                ...fileDoc,
                uploadedAt: serverTimestamp()
              });

              await this.addFileChangeRecord(
                fileData.companyId,
                fileData.shipmentId,
                fileData.uploadedBy,
                'CREATE',
                fileData.fileName
              );

              resolve({
                ...fileDoc,
                id: docRef.id
              });
            } catch (error) {
              console.error('Error creating file document:', error);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading shipment file:', error);
      throw error;
    }
  }

  async createShipmentFileFromUrl(
    data: {
      shipmentId: string
      companyId: string
      fileType: string
      fileName: string
      fileUrl: string
      fileSize: number
      mimeType: string
      uploadedBy: { id: string; name: string; lastName?: string }
    }
  ): Promise<void> {
    const filesCollectionRef = collection(
      this.db,
      `Companies/${data.companyId}/shipments/${data.shipmentId}/files`
    )
    await addDoc(filesCollectionRef, {
      shipmentId: data.shipmentId,
      companyId: data.companyId,
      fileType: data.fileType,
      fileName: data.fileName,
      fileUrl: data.fileUrl,
      fileSize: data.fileSize,
      mimeType: data.mimeType,
      uploadedBy: data.uploadedBy,
      uploadedAt: serverTimestamp(),
    })

    await this.addFileChangeRecord(
      data.companyId,
      data.shipmentId,
      data.uploadedBy,
      'CREATE',
      data.fileName
    )
  }

  /**
   * Delete a file from a shipment
   */
  async deleteShipmentFile(
    shipmentId: string,
    fileId: string,
    companyId: string,
    deletedBy?: { id: string; name: string; lastName?: string }
  ): Promise<void> {
    try {
      // Get file document to get the storage path
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );
      
      const fileDoc = await getDoc(fileDocRef);
      
      if (!fileDoc.exists()) {
        throw new Error('File not found');
      }

      const fileData = fileDoc.data();

      const filePath = shipmentFileStoragePath(fileData, companyId, shipmentId);

      /**
       * El objeto solo se borra si nadie más lo usa. La ruta se arma con el
       * nombre del archivo, así que el mismo nombre subido dos veces al mismo
       * embarque deja dos documentos apuntando a un solo objeto: borrarlo con el
       * primero dejaba al segundo señalando a la nada.
       *
       * Si la consulta falla se conserva el objeto y se sigue: un objeto
       * huérfano en Storage cuesta centavos, un documento sin archivo rompe la
       * pantalla.
       */
      let compartido = true;
      try {
        const hermanos = await getDocs(
          collection(this.db, `Companies/${companyId}/shipments/${shipmentId}/files`)
        );
        compartido = storagePathIsShared(
          hermanos.docs.filter(d => d.id !== fileId).map(d => d.data()),
          filePath,
          companyId,
          shipmentId
        );
      } catch (error) {
        console.warn('No se pudo verificar si otro documento usa el archivo; se conserva en Storage:', error);
      }

      if (compartido) {
        console.info(`Otro documento del embarque usa ${filePath}: se conserva en Storage.`);
      } else {
        const fileRef = storageRef(getStorage(), filePath);
        try {
          await deleteObject(fileRef);
        } catch (storageError: any) {
          if (storageError?.code === 'storage/object-not-found') {
            console.warn('⚠️ Archivo no encontrado en Storage, continuando con eliminación del documento en Firestore');
          } else {
            throw storageError;
          }
        }
      }

      // Delete from Firestore
      await deleteDoc(fileDocRef);

      if (deletedBy) {
        await this.addFileChangeRecord(
          companyId,
          shipmentId,
          deletedBy,
          'DELETE',
          fileData.fileName
        );
      }
    } catch (error) {
      console.error('Error deleting shipment file:', error);
      throw error;
    }
  }

  /**
   * Update file type for a shipment file
   */
  async updateShipmentFileType(
    shipmentId: string,
    fileId: string,
    fileType: string,
    companyId: string,
    additionalFileTypes: string[] = []
  ): Promise<void> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );

      await updateDoc(fileDocRef, {
        fileType: fileType,
        additionalFileTypes,
        updatedAt: new Date()
      });
      
    } catch (error) {
      console.error('Error updating shipment file type:', error);
      throw error;
    }
  }

  /**
   * Cambia el nombre que se muestra del documento. Solo es visual: `fileName`
   * y el objeto en Storage no se tocan. Vacío borra el campo y vuelve a
   * mostrarse `fileName`.
   */
  async updateShipmentFileDisplayName(
    shipmentId: string,
    fileId: string,
    companyId: string,
    displayName: string
  ): Promise<void> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );

      const trimmed = displayName.trim();
      await updateDoc(fileDocRef, {
        displayName: trimmed || deleteField(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating shipment file display name:', error);
      throw error;
    }
  }

  /**
   * Guarda la extracción de un tipo de documento adicional sobre el mismo archivo
   * (documento combinado). No toca extractedData, que es del tipo principal.
   */
  async saveShipmentFileAdditionalExtraction(
    shipmentId: string,
    fileId: string,
    documentType: string,
    data: any,
    companyId: string
  ): Promise<void> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );
      await updateDoc(fileDocRef, {
        [`additionalExtractedData.${documentType}`]: data,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving additional extraction for shipment file:', error);
      throw error;
    }
  }

  /**
   * Get a single file by ID
   */
  async getShipmentFileById(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<ShipmentFile | null> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );
      
      const fileDoc = await getDoc(fileDocRef);
      
      if (!fileDoc.exists()) {
        return null;
      }

      const data = fileDoc.data();
      return {
        id: fileDoc.id,
        shipmentId: data.shipmentId,
        fileType: data.fileType,
        fileName: data.fileName,
        displayName: data.displayName,
        fileUrl: data.fileUrl,
        fileSize: data.fileSize,
        mimeType: data.mimeType,
        uploadedAt: data.uploadedAt?.toDate() || new Date(),
        uploadedBy: data.uploadedBy,
        companyId: data.companyId,
        reviewedBy: data.reviewedBy ? {
          uid: data.reviewedBy.uid,
          name: data.reviewedBy.name,
          lastName: data.reviewedBy.lastName,
          reviewedAt: data.reviewedBy.reviewedAt?.toDate?.() || data.reviewedBy.reviewedAt
        } : null,
      };
    } catch (error) {
      console.error('Error getting shipment file by ID:', error);
      throw error;
    }
  }

  async markShipmentFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string,
    reviewer: { uid: string; name: string; lastName: string }
  ): Promise<void> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );

      await updateDoc(fileDocRef, {
        reviewedBy: {
          uid: reviewer.uid,
          name: reviewer.name,
          lastName: reviewer.lastName,
          reviewedAt: new Date()
        }
      });
    } catch (error) {
      console.error('Error marking shipment file as reviewed:', error);
      throw error;
    }
  }

  async unmarkShipmentFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<void> {
    try {
      const fileDocRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );

      await updateDoc(fileDocRef, {
        reviewedBy: null
      });
    } catch (error) {
      console.error('Error unmarking shipment file review:', error);
      throw error;
    }
  }

  // ===================================
  // DOCUMENT FOLDERS MANAGEMENT
  // ===================================

  async getDocumentFolders(
    shipmentId: string,
    companyId: string
  ): Promise<import('@/domain/entities/ShipmentFile').ShipmentDocumentFolder[]> {
    try {
      const foldersRef = collection(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/DocumentFolders`
      );
      const snapshot = await getDocs(foldersRef);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as import('@/domain/entities/ShipmentFile').ShipmentDocumentFolder[];
    } catch (error) {
      console.error('Error getting document folders:', error);
      throw error;
    }
  }

  async createDocumentFolder(
    shipmentId: string,
    companyId: string,
    name: string,
    parentFolderId: string | null,
    createdBy: string
  ): Promise<import('@/domain/entities/ShipmentFile').ShipmentDocumentFolder> {
    try {
      const foldersRef = collection(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/DocumentFolders`
      );
      const folderData = {
        name,
        parentFolderId,
        shipmentId,
        createdBy,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(foldersRef, folderData);
      return { id: docRef.id, ...folderData, createdAt: new Date() };
    } catch (error) {
      console.error('Error creating document folder:', error);
      throw error;
    }
  }

  async renameDocumentFolder(
    shipmentId: string,
    companyId: string,
    folderId: string,
    newName: string
  ): Promise<void> {
    try {
      const folderRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/DocumentFolders/${folderId}`
      );
      await updateDoc(folderRef, { name: newName });
    } catch (error) {
      console.error('Error renaming document folder:', error);
      throw error;
    }
  }

  async updateDocumentFolderParent(
    shipmentId: string,
    companyId: string,
    folderId: string,
    parentFolderId: string | null
  ): Promise<void> {
    try {
      const folderRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/DocumentFolders/${folderId}`
      );
      await updateDoc(folderRef, { parentFolderId });
    } catch (error) {
      console.error('Error updating document folder parent:', error);
      throw error;
    }
  }

  async deleteDocumentFolder(
    shipmentId: string,
    companyId: string,
    folderId: string
  ): Promise<void> {
    try {
      const folderRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/DocumentFolders/${folderId}`
      );
      await deleteDoc(folderRef);
    } catch (error) {
      console.error('Error deleting document folder:', error);
      throw error;
    }
  }

  async moveFileToFolder(
    shipmentId: string,
    companyId: string,
    fileId: string,
    folderId: string | null
  ): Promise<void> {
    try {
      const fileRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
      );
      await updateDoc(fileRef, { folderId });
    } catch (error) {
      console.error('Error moving file to folder:', error);
      throw error;
    }
  }

  /**
   * Mueve varios archivos a la misma carpeta (null = raíz) con escrituras en
   * lote. Firestore acepta hasta 500 operaciones por batch, así que se parte
   * en grupos de ese tamaño.
   */
  async moveFilesToFolder(
    shipmentId: string,
    companyId: string,
    fileIds: string[],
    folderId: string | null
  ): Promise<void> {
    try {
      for (let i = 0; i < fileIds.length; i += 500) {
        const batch = writeBatch(this.db);
        for (const fileId of fileIds.slice(i, i + 500)) {
          const fileRef = doc(
            this.db,
            `Companies/${companyId}/shipments/${shipmentId}/files/${fileId}`
          );
          batch.update(fileRef, { folderId });
        }
        await batch.commit();
      }
    } catch (error) {
      console.error('Error moving files to folder:', error);
      throw error;
    }
  }

  // ===================================
  // INVOICES MANAGEMENT
  // ===================================

  /**
   * Obtener todas las facturas de un embarque
   */
  async getShipmentInvoices(shipmentId: string, companyId: string): Promise<any[]> {
    try {
      const invoicesRef = collection(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/invoices`
      );
      
      const querySnapshot = await getDocs(invoicesRef);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting shipment invoices:', error);
      throw error;
    }
  }

  /**
   * Obtener una factura específica por ID
   */
  async getInvoiceById(
    shipmentId: string,
    invoiceId: string,
    companyId: string
  ): Promise<any> {
    try {
      const invoiceRef = doc(
        this.db,
        `Companies/${companyId}/shipments/${shipmentId}/invoices/${invoiceId}`
      );
      
      const invoiceDoc = await getDoc(invoiceRef);
      
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found');
      }
      
      return {
        id: invoiceDoc.id,
        ...invoiceDoc.data()
      };
    } catch (error) {
      console.error('Error getting invoice by ID:', error);
      throw error;
    }
  }

  // ===================================
  // CUSTOMS DECLARATIONS (subcolección customs/)
  // ===================================

  async addCustomsDeclaration(
    shipmentId: string,
    data: any,
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<string> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const customsCollectionRef = collection(shipmentRef, 'customs')

    if (data.declarationNumber) {
      // Duplicado solo si existe una declaración con el mismo número Y los mismos valores
      // en los demás campos provistos. Un mismo número con valores distintos es válido
      // (p.ej. varias partidas/fracciones de una misma predeclaración).
      const normalize = (v: any) => v?.toString().trim().toLowerCase() ?? ''
      const comparableKeys = Object.keys(data).filter(
        key => !['id', 'createdAt', 'updatedAt'].includes(key)
          && data[key] !== null && data[key] !== undefined && data[key] !== ''
      )
      const existingSnapshot = await getDocs(customsCollectionRef)
      const duplicated = existingSnapshot.docs.some(d => {
        const existing = d.data()
        if (normalize(existing.declarationNumber) !== normalize(data.declarationNumber)) return false
        return comparableKeys.every(key => normalize(existing[key]) === normalize(data[key]))
      })
      if (duplicated) {
        throw new Error(`La declaración "${data.declarationNumber}" ya existe en este embarque`)
      }
    }

    const newCustomsRef = doc(customsCollectionRef)
    const batch = writeBatch(this.db)
    const now = Timestamp.now()

    const cleanedData = this.cleanObject(this.convertDateFieldsToTimestamp(data))

    const customsToSave = {
      ...cleanedData,
      id: newCustomsRef.id,
      createdAt: now,
      updatedAt: now
    }

    batch.set(newCustomsRef, customsToSave)

    if (userId && name && lastName) {
      batch.update(shipmentRef, {
        updatedAt: now,
        updatedBy: { id: userId, name, lastName }
      })

      const changesCollectionRef = collection(shipmentRef, 'changesHistory')
      const changeData = {
        timestamp: now,
        type: 'CUSTOMS_ADDED',
        userId,
        name,
        lastName,
        action: 'CREATE',
        description: data.declarationNumber
          ? `Agregó la declaración aduanal ${data.declarationNumber}`
          : 'Agregó una declaración aduanal',
        changes: [{
          field: `customs.${newCustomsRef.id}`,
          oldValue: null,
          newValue: this.cleanObject(data)
        }]
      }

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      })
    }

    await batch.commit()
    return newCustomsRef.id
  }

  async updateCustomsDeclaration(
    shipmentId: string,
    customsId: string,
    data: any,
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string
  ): Promise<void> {
    if (!shipmentId || !customsId || !companyId) {
      throw new Error('Shipment ID, Customs ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const customsCollectionRef = collection(shipmentRef, 'customs')
    const customsRef = doc(customsCollectionRef, customsId)

    const customsSnap = await getDoc(customsRef)
    if (!customsSnap.exists()) {
      throw new Error('Customs declaration not found')
    }
    const oldData = customsSnap.data()

    if (data.declarationNumber && data.declarationNumber !== oldData.declarationNumber) {
      const existingSnapshot = await getDocs(customsCollectionRef)
      const duplicated = existingSnapshot.docs.some(d =>
        d.id !== customsId &&
        d.data().declarationNumber?.toString().toLowerCase() === data.declarationNumber.toString().toLowerCase()
      )
      if (duplicated) {
        throw new Error(`La declaración "${data.declarationNumber}" ya existe en este embarque`)
      }
    }

    const batch = writeBatch(this.db)
    const now = Timestamp.now()

    const cleanedData = this.cleanObject(this.convertDateFieldsToTimestamp(data))

    batch.update(customsRef, {
      ...cleanedData,
      updatedAt: now
    })

    if (userId && name && lastName) {
      batch.update(shipmentRef, {
        updatedAt: now,
        updatedBy: { id: userId, name, lastName }
      })

      const changes = Object.keys(data)
        .map(field => {
          const oldValue = oldData[field]
          const newValue = data[field]
          if (this.areValuesEqual(oldValue, newValue)) return null
          const cleanOldValue = oldValue === undefined || oldValue === null ? null : this.cleanObject(oldValue)
          const cleanNewValue = newValue === undefined || newValue === null ? null : this.cleanObject(newValue)
          return {
            field: `customs.${customsId}.${field}`,
            oldValue: cleanOldValue,
            newValue: cleanNewValue
          }
        })
        .filter((change): change is NonNullable<typeof change> =>
          change !== null &&
          !this.areValuesEqual(change.oldValue, change.newValue)
        )

      if (changes.length > 0) {
        const changesCollectionRef = collection(shipmentRef, 'changesHistory')
        const changeData: any = {
          timestamp: Timestamp.fromDate(new Date()),
          type: 'CUSTOMS_UPDATED',
          userId,
          name,
          lastName,
          action: 'UPDATE',
          description: data.declarationNumber
            ? `Actualizó la declaración aduanal ${data.declarationNumber}`
            : 'Actualizó una declaración aduanal',
          changes
        }

        Object.keys(changeData).forEach(key => {
          if (changeData[key] === undefined) {
            delete changeData[key]
          }
        })

        await addDoc(changesCollectionRef, changeData)
      }
    }

    await batch.commit()
  }

  async deleteCustomsDeclaration(
    shipmentId: string,
    customsId: string,
    companyId?: string,
    userId?: string,
    name?: string,
    lastName?: string,
    description?: string
  ): Promise<void> {
    if (!shipmentId || !customsId || !companyId) {
      throw new Error('Shipment ID, Customs ID, and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const customsRef = doc(collection(shipmentRef, 'customs'), customsId)

    const customsSnap = await getDoc(customsRef)
    if (!customsSnap.exists()) {
      throw new Error('Customs declaration not found')
    }
    const customsData = customsSnap.data()

    await deleteDoc(customsRef)

    if (userId && name && lastName) {
      const changesCollectionRef = collection(shipmentRef, 'changesHistory')
      const changeData: ShipmentChange = {
        timestamp: new Date(),
        userId,
        name,
        lastName,
        action: 'DELETE',
        description: description || (customsData.declarationNumber
          ? `Eliminó la declaración aduanal ${customsData.declarationNumber}`
          : 'Eliminó una declaración aduanal'),
        changes: [{
          field: `customs.${customsId}`,
          oldValue: this.cleanObject(customsData),
          newValue: null
        }]
      }

      await addDoc(changesCollectionRef, {
        ...changeData,
        timestamp: serverTimestamp()
      })

      await updateDoc(shipmentRef, {
        lastUpdate: serverTimestamp(),
        updatedBy: { id: userId, name, lastName }
      })
    }
  }

  async listenToCustomsDeclarations(
    shipmentId: string,
    callback: (declarations: any[]) => void,
    companyId?: string
  ): Promise<() => void> {
    if (!shipmentId || !companyId) {
      throw new Error('Shipment ID and Company ID are required')
    }
    const shipmentRef = doc(this.getCollectionRef(companyId), shipmentId)
    const customsCollectionRef = collection(shipmentRef, 'customs')

    return onSnapshot(customsCollectionRef, (snapshot) => {
      const declarations = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        }
      })
      callback(declarations)
    })
  }

  private convertDateFieldsToTimestamp(data: any): any {
    const dateFieldNames = [
      'declarationAcceptanceDate',
      'releaseDate',
      'transportDocumentDate',
      'cargoManifestDate',
      'supplierInvoiceDate',
      'bankPresentationDate'
    ]
    const result: any = { ...data }
    for (const field of dateFieldNames) {
      const value = result[field]
      if (value === undefined || value === null || value === '') continue
      if (typeof value === 'string') {
        const parsed = new Date(value)
        if (!isNaN(parsed.getTime())) {
          result[field] = Timestamp.fromDate(parsed)
        }
      } else if (value instanceof Date) {
        result[field] = Timestamp.fromDate(value)
      }
    }
    return result
  }

  // ===== Consolidation Methods =====

  async findMasters(companyId: string): Promise<Shipment[]> {
    const q = query(
      this.getCollectionRef(companyId),
      where('consolidationType', '==', 'master')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => this.fromFirestore({ id: d.id, ...d.data() }));
  }

  async findHousesByMaster(masterShipmentId: string, companyId: string): Promise<Shipment[]> {
    const q = query(
      this.getCollectionRef(companyId),
      where('masterShipmentId', '==', masterShipmentId),
      where('consolidationType', '==', 'house')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => this.fromFirestore({ id: d.id, ...d.data() }));
  }

  async createHouseFromMaster(
    masterShipmentId: string,
    houseData: Partial<Shipment>,
    companyId: string,
    userId: string
  ): Promise<Shipment> {
    const collectionRef = this.getCollectionRef(companyId);

    const counters = await this.getAndIncrementCounterBatch(companyId, 1);
    const liberationNumber = counters[0].toString().padStart(8, '0');
    const idNumber = `HSE-${counters[0].toString().padStart(7, '0')}`;

    return await runTransaction(this.db, async (transaction) => {
      const masterRef = doc(collectionRef, masterShipmentId);
      const masterSnap = await transaction.get(masterRef);

      if (!masterSnap.exists()) {
        throw new Error('Master shipment not found');
      }

      const masterData = masterSnap.data();
      if (masterData.consolidationType !== 'master') {
        throw new Error('Referenced shipment is not a master');
      }

      const houseRef = doc(collectionRef);
      const rawHouse: any = {
        ...houseData,
        id: houseRef.id,
        liberationNumber,
        idNumber,
        consolidationType: 'house',
        masterShipmentId,
        masterShipmentNumber: masterData.idNumber || masterData.liberationNumber || null,
        origin: houseData.origin ?? masterData.origin ?? null,
        destination: houseData.destination ?? masterData.destination ?? null,
        originLocationCode: houseData.originLocationCode ?? masterData.originLocationCode ?? null,
        originLocationName: houseData.originLocationName ?? masterData.originLocationName ?? null,
        destinationLocationCode: houseData.destinationLocationCode ?? masterData.destinationLocationCode ?? null,
        destinationLocationName: houseData.destinationLocationName ?? masterData.destinationLocationName ?? null,
        internationalCarrier: houseData.internationalCarrier ?? masterData.internationalCarrier ?? null,
        shippingLine: houseData.shippingLine ?? masterData.shippingLine ?? null,
        transportMode: houseData.transportMode ?? masterData.transportMode ?? null,
        milestones: {
          ...(masterData.milestones || {}),
          ...(houseData.milestones || {}),
        },
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: houseData.status || masterData.status || 'BOOKING_REQUEST',
      };

      const newHouse = Object.fromEntries(
        Object.entries(rawHouse).map(([k, v]) => [k, v === undefined ? null : v])
      );

      transaction.set(houseRef, this.toFirestore(newHouse));

      const houseRef_ = {
        id: houseRef.id,
        idNumber,
        hbl: newHouse.hbl || null,
        hawb: newHouse.hawb || null,
        status: newHouse.status || null,
        consignee: newHouse.consignee?.name || null,
      };

      transaction.update(masterRef, {
        houseCount: (masterData.houseCount || 0) + 1,
        houses: arrayUnion(houseRef_),
        updatedAt: new Date()
      });

      return this.fromFirestore(newHouse);
    });
  }

  async unlinkHouseFromMaster(
    houseShipmentId: string,
    masterShipmentId: string,
    companyId: string
  ): Promise<void> {
    const collectionRef = this.getCollectionRef(companyId);

    await runTransaction(this.db, async (transaction) => {
      const houseRef = doc(collectionRef, houseShipmentId);
      const masterRef = doc(collectionRef, masterShipmentId);

      const [houseSnap, masterSnap] = await Promise.all([
        transaction.get(houseRef),
        transaction.get(masterRef)
      ]);

      if (!houseSnap.exists()) throw new Error('House shipment not found');
      if (!masterSnap.exists()) throw new Error('Master shipment not found');

      const masterData = masterSnap.data();

      transaction.update(houseRef, {
        consolidationType: 'standalone',
        masterShipmentId: null,
        masterShipmentNumber: null,
        updatedAt: new Date()
      });

      transaction.update(masterRef, {
        houseCount: Math.max((masterData.houseCount || 1) - 1, 0),
        updatedAt: new Date()
      });
    });
  }

  async addHouseRefToMaster(
    masterShipmentId: string,
    houseRef: { id: string; idNumber: string; hbl?: string | null; hawb?: string | null; status?: string; consignee?: string | null },
    companyId: string
  ): Promise<void> {
    const collectionRef = this.getCollectionRef(companyId);
    const masterDocRef = doc(collectionRef, masterShipmentId);
    const masterDoc = await getDoc(masterDocRef);
    if (isShipmentLocked(masterDoc.data())) {
      throw new ShipmentLockedError(masterShipmentId);
    }
    await updateDoc(masterDocRef, {
      houses: arrayUnion(houseRef),
      houseCount: (masterDoc.data()?.houseCount || 0) + 1,
      updatedAt: new Date()
    });
  }

  /**
   * Escribe la reactivación de un embarque completado: es la ÚNICA vía para
   * escribir sobre un completado desde el front. No reutiliza `updateShipment`
   * ni relaja su guarda general, que sigue cerrada para todo lo demás.
   */
  async reactivateShipment(shipmentId: string, companyId: string, actor: ShipmentActor): Promise<void> {
    const docRef = doc(this.getCollectionRef(companyId), shipmentId);
    const currentDoc = await getDoc(docRef);
    if (!currentDoc.exists()) {
      throw new Error('Shipment not found');
    }
    if (!isShipmentLocked(currentDoc.data())) {
      throw new Error('SHIPMENT_NOT_COMPLETED');
    }

    await updateDoc(docRef, {
      isShipmentCompleted: false,
      lifecycleChange: { by: actor.email || actor.uid },
      lastUpdate: new Date(),
      updatedBy: { id: actor.uid, name: actor.name || '', lastName: actor.lastName || '' }
    });

    await addDoc(collection(docRef, 'changesHistory'), {
      timestamp: Timestamp.now(),
      userId: actor.uid,
      name: actor.name || '',
      lastName: actor.lastName || '',
      action: 'UPDATE',
      description: 'Reactivó el embarque',
      changes: [{ field: 'isShipmentCompleted', oldValue: currentDoc.data()?.isShipmentCompleted ?? null, newValue: false }]
    });
  }
}